// eslint-disable-next-line no-restricted-imports
import { type SortDescriptor } from "@progress/kendo-data-query";
import { message, Space } from "antd";
import { LineItemType } from "legacyComponents/LineItemContainer.types";
import { isEqual } from "lodash-es";
import type moment from "moment";
import { Component, useReducer } from "react";
import { type RouteComponentProps } from "react-router";

import { IconCopy, IconPrinter, IconTrash } from "@btnet/blueprint-icons";
import {
    BTDropdownButton,
    BTFloatingActionBar,
    BTSplitButton,
    type BTSplitButtonItem,
} from "@btnet/core-components";
import {
    billsExportToAccountingPostV2,
    billsGridPermissionsPostV2,
    billsMassSendForApprovalPutV2,
    billsTabCountsPostV2,
} from "@btnet/services-btnet/handlers/bills/bills";
import { BillDocumentType } from "@btnet/services-btnet/handlers/models/billDocumentType";
import type { BillsGridPermissionsResponse } from "@btnet/services-btnet/handlers/models/billsGridPermissionsResponse";
import { IntegrationTypes } from "@btnet/services-btnet/handlers/models/integrationTypes";

import {
    BdsActionBar,
    BdsButtonGroup,
    BdsContentHeader,
    BdsIconBills,
    BdsIconEnvelopeSimple,
    BdsIconExport,
    BdsIconFunnel,
    BdsIconHelp,
    BdsIconPlus,
    BdsRollupButton,
} from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { type IFilterValues } from "helpers/globalContext/FilterValueContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { useWebviewContext } from "helpers/webview/WebviewContext";

import { type BTSelectItem, type IBaseEntity } from "types/apiResponse/apiResponse";
import { BTLocalStorage } from "types/btStorage";
import {
    type AccountingIntegrationType,
    AccountingValidationStatus,
    TabPermissions,
} from "types/enum";

import { dispatchTracking } from "utilities/analytics/analytics";
import { showAPIErrorMessage, UpgradeRequiredError } from "utilities/apiHandler";
import {
    getFileNameFromAxiosResponse,
    triggerBrowserDownload,
} from "utilities/document/fileDownload.utils";
import { reportError } from "utilities/errorHelpers";
import { setLoadingAction } from "utilities/form/form";
import {
    filterSelectedGridColumns,
    getGridUpdateDataForFullReload,
    getUpdatedGridState,
    type IGridUpdateData,
    isGridErrorMessageApiError,
    mapGridStateToRequest,
    mapSavedViewToRequest,
    showGridErrorMessage,
} from "utilities/grid/grid";
import {
    GridRequestCanceller,
    isCancelledGridRequestError,
} from "utilities/grid/useCancellableGridRequest";
import { getClosestModal } from "utilities/helpers";
import { MANAGE_SAVED_VIEWS } from "utilities/historyState/actionWithHistory.utility";
import { useHistoryState } from "utilities/historyState/useHistoryState";
import {
    EmptyStateEntity,
    getDefaultPagingData,
    getFilterEntityType,
    type IHasEmptyStateBannerProps,
    type IHasEmptyStateBannerState,
    ListEntityType,
    PagingData,
} from "utilities/list/list.types";
import { isInPortal } from "utilities/portal/portal";
import { retryPolicy } from "utilities/retryPolicy/retryPolicy";
import { routes } from "utilities/routes";
import { scrollToPosition } from "utilities/scroll/scroll";
import { bulkActionToast } from "utilities/toast/bulkActionToast";

import { BdsListPageHeaderFilterButton } from "commonComponents/bdsWrappers/BdsListPageHeader/BdsListPageHeaderFilterButton";
import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { btDeleteConfirm, btError } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTListHeader } from "commonComponents/btWrappers/BTListHeader/BTListHeader";
import {
    BTListLayoutWhitespace,
    ListSection,
} from "commonComponents/btWrappers/BTListLayout/BTListLayout";
import { BTListLayoutBDS } from "commonComponents/btWrappers/BTListLayoutBDS/BTListLayoutBDS";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import {
    BTPagination,
    getDefaultPageSize,
    type IBTPaginationHistoryState,
} from "commonComponents/btWrappers/BTPagination/BTPagination";
import { EmptyStateHandler } from "commonComponents/entity/emptyState/common/EmptyState.api.handler";
import { type EmptyStateActions } from "commonComponents/entity/emptyState/common/EmptyState.api.types";
import { RouteExistingOwnerInvoiceSelect } from "commonComponents/entity/invoicing/ExistingOwnerInvoiceSelect/RouteExistingOwnerInvoiceSelect";
import { type ILineItemsToInvoiceHandler } from "commonComponents/entity/invoicing/LineItemsToInvoice/LineItemsToInvoice.api.handler";
import {
    type ICheckedActionInvoiceData,
    type ILineItemEntityDetails,
    type ILineItemsToInvoiceDetails,
    mapResponseToInvoiceData,
} from "commonComponents/entity/invoicing/LineItemsToInvoice/LineItemsToInvoice.api.types";
import { RouteLineItemsToInvoice } from "commonComponents/entity/invoicing/LineItemsToInvoice/RouteLineItemsToInvoice";
import {
    type ISelectedEntityCopyFormValues,
    type SelectedEntityCopyFormActions,
} from "commonComponents/entity/list/SelectedEntityCopy/SelectedEntityCopy/SelectedEntityCopy.api.types";
import { RouteSelectedEntityCopy } from "commonComponents/entity/list/SelectedEntityCopy/SelectedEntityCopy/SelectedEntityCopyRoute";
import {
    type IHasUpsellState,
    PackageUpsellList,
} from "commonComponents/entity/marketing/PackageUpsellList/PackageUpsellList";
import { JobViewingPermissionWizard } from "commonComponents/entity/permissionWizard/JobViewingPermissionWizard/JobViewingPermissionWizard";
import { OpenedFromEntity } from "commonComponents/entity/permissionWizard/JobViewingPermissionWizard/JobViewingPermissionWizard.api.types";
import PermissionWizard from "commonComponents/entity/permissionWizard/PermissionWizard/PermissionWizard";
import { PayOnlineMarketingModal } from "commonComponents/financial/PaymentsOverview/PayOnlineMarketingModal";
import PaymentVoidConfirm, {
    type PaymentVoidConfirmFormActions,
} from "commonComponents/financial/PaymentVoidConfirm/PaymentVoidConfirm";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { withJobsitesRequired } from "commonComponents/helpers/JobsitesRequired/JobsitesRequired";
import { ActionCard } from "commonComponents/utilities/ActionCard/ActionCard";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { getBillColumnImplementations } from "commonComponents/utilities/Grid/bill/billColumnImplementations";
import { BillLinkAbsoluteContext } from "commonComponents/utilities/Grid/bill/billLinkMode";
import { standardColumnImplementations } from "commonComponents/utilities/Grid/common/columnImplementations/standardColumnImplementations";
import { GridSettingsHandler } from "commonComponents/utilities/Grid/common/GridSettings/GridSettings.api.handler";
import {
    type GridViewColumn,
    type GridViewItem,
    type IGridSettingsFormValues,
} from "commonComponents/utilities/Grid/common/GridSettings/GridSettings.api.types";
import { ManageSavedViews } from "commonComponents/utilities/Grid/common/ManageSavedViews/ManageSavedViews";
import {
    defaultGridEntity,
    defaultGridState,
    GridContainer,
    gridStateManager,
    initialGridState,
} from "commonComponents/utilities/Grid/GridContainer";
import {
    type GridColumn,
    type IListWithGridProps,
    type IListWithGridState,
    type ToolbarDownloadAction,
} from "commonComponents/utilities/Grid/GridContainer.types";
import { JobIdTypes } from "commonComponents/utilities/JobPicker/JobPicker.types";
import { ListPageFooter } from "commonComponents/utilities/ListPageFooter/ListPageFooter";
import {
    deleteSearchEntity,
    SearchCategory,
} from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.api.types";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";

import { RouteBill } from "entity/bill/Bill/RouteBill";
import { ApplyLienWaiver } from "entity/bill/BillList/ApplyLienWaiver/ApplyLienWaiver";
import { BillFullPageBanner } from "entity/bill/BillList/BillFullPageBanner";
import { MassUpdateAccountingStatus } from "entity/bill/BillList/MassUpdateAccountingStatus/MassUpdateAccountingStatus";
import { SendToAccountingBulkAction } from "entity/bill/BillList/SendToAccountingBulkAction/SendToAccountingBulkAction";
import TabsFilter, {
    isTabsFilterTab,
    type ITabCounts,
    TabsFilterTabs,
} from "entity/bill/BillList/TabsFilter/TabsFilter";
import {
    canApplyLienWaiver,
    canMarkReadyForPayment,
    canRecordOfflinePayment,
    getSelectionDocType,
    type IBillRowSelection,
    isVendorCredit,
} from "entity/bill/BillList/utils/billFloatingBarActionEligibility";
import { getCopyBillsToastMessage } from "entity/bill/BillList/utils/copyBillsToastMessage";
import { BillFilterKey, BillListRouteView } from "entity/bill/common/BillListEnums";
import { BillStatusType } from "entity/bill/common/BillStatus/BillStatus";
import { getBillBulkSummary } from "entity/bill/common/getBillToastMessage";
import {
    type BillViewModePreference,
    getBillViewModePreference,
} from "entity/bill/common/resolveBillViewMode";
import { performBulkApprove } from "entity/bill/shared/performBulkApprove";
import { RouteConversation } from "entity/conversation/Conversation/ConversationRoute";
import { FilterHandler } from "entity/filters/Filter/Filter.api.handler";
import {
    type FilterEntity,
    type FilterFormActions,
    getSelectedFilterItemsFromSavedFilter,
    type IFilterFormValues,
    type SelectedFilterItem,
} from "entity/filters/Filter/Filter.api.types";
import {
    defaultFilterState,
    type FilterActions,
    FilterReducer,
    type IFilterReducerState,
    SetUnsavedAppliedFilter,
    UpdateUnsavedChanges,
} from "entity/filters/Filter/Filter.reducer";
import { FilterDrawer } from "entity/filters/Filter/FilterDrawer";
import {
    applySerializeBehavior,
    getAppliedFilterCount,
    getNullableFilterString,
} from "entity/filters/filters.utils";
import { type SavedFilterItem } from "entity/filters/SavedFilter/SavedFilter.api.types";
import { type SavedViewsAndFiltersSettingsState } from "entity/filters/SavedViewsAndFiltersList/SavedViewsAndFiltersCard/SavedViewsAndFiltersCard.api.types";
import { SavedViewsAndFiltersSettings } from "entity/filters/SavedViewsAndFiltersList/SavedViewsAndFiltersSettings/SavedViewsAndFiltersSettings";
import {
    type ISavedViewsSettingsStateProps,
    withSavedViewsSettingsState,
} from "entity/filters/SavedViewsAndFiltersList/withSavedViewsSettingsState";
import {
    type IMassBillPaymentHandler,
    MassBillPaymentHandler,
} from "entity/massPoPayment/MassBillPayment/MassBillPayment.api.handler";
import { RouteMessageCompose } from "entity/message/MessageCompose/MessageComposeRoute";
import { RouteOwnerInvoice } from "entity/ownerInvoice/OwnerInvoice/OwnerInvoiceRoute";
import { PurchaseOrderTab } from "entity/purchaseOrder/common/PurchaseOrderTab/PurchaseOrderTab";
import { RoutePurchaseOrder } from "entity/purchaseOrder/PurchaseOrder/PurchaseOrderRoute";
import { PurchaseOrderTabTypes } from "entity/purchaseOrder/PurchaseOrderList/PurchaseOrderList.api.types";
import { CostInboxMigrationBanner } from "entity/receipt/CostInboxMigration/CostInboxMigrationBanner";
import EmailModal from "entity/receipt/ReceiptList/EmailModal/EmailModal";
import { RouteTemplateImport } from "entity/templateImport/TemplateImport/TemplateImportRoute";
import { BillPaymentRoute } from "entity/unifiedBillPayment/UnifiedBillPayment/BillPaymentRoute";

import "./BillList.less";

import { BillDraftsEmptyState } from "./BillEmptyState/BillDraftsEmptyState";
import { BillEmptyState } from "./BillEmptyState/BillEmptyState";
import { BillEmptyStateBanner } from "./BillEmptyState/BillEmptyStateBanner";
import { BillListHandler } from "./BillList.api.handler";
import {
    type ApplyLienWaiverResponse,
    type BillGridEntity,
    type BillListFormActions,
    type CanSendEntitiesToAccountingResponse,
    type MassVoidAlertInfo,
} from "./BillList.api.types";
import { getBillAgColumnImplementations } from "./BillListAgGrid/billAgColumnImplementations";
import { BillListAgGrid } from "./BillListAgGrid/BillListAgGrid";
import BulkBillUploadModal from "./BulkBillUpload/BulkBillUploadModal";
import { ALLOWED_BILL_UPLOAD_FILE_TYPES } from "./BulkBillUpload/BulkBillUploadModal.api.types";
import { BulkBillUploadTracker, type IUploadGroup } from "./BulkBillUpload/BulkBillUploadTracker";
import { InboxDropzoneBar } from "./InboxDropzone/InboxDropzoneBar";
import { InboxDropzoneWrapper } from "./InboxDropzone/InboxDropzoneWrapper";

export type IBillListHistoryState = IBTPaginationHistoryState;

interface IBillListProps
    extends RouteComponentProps<{ view?: string }>,
        IListWithGridProps<BillListHandler>,
        IHasEmptyStateBannerProps {
    jobIds: number[];
    jobName: string;
    builderId: number;
    invoiceHandler?: ILineItemsToInvoiceHandler;
    massBillPaymentHandler?: IMassBillPaymentHandler;
    appliedFilters?: IFilterValues;
    isXsScreen: boolean;
    isDraftBillsEffective?: boolean;
    isAllJobsSelected: boolean;
}

interface IBillListInternalProps extends IBillListProps, ISavedViewsSettingsStateProps {
    filterReducerState: IFilterReducerState;
    filterReducerDispatch: React.Dispatch<FilterActions>;
    userInfo: React.ContextType<typeof UserInfoContext>;
    isWebview: boolean;
    webviewContext?: ReturnType<typeof useWebviewContext>;
    historyState: IBillListHistoryState;
}

interface IBillListState
    extends IListWithGridState<BillGridEntity>,
        IHasEmptyStateBannerState,
        IHasUpsellState {
    previousFilters?: IFilterFormValues;
    actionBeingPerformed:
        | BillListFormActions
        | EmptyStateActions
        | FilterFormActions
        | PaymentVoidConfirmFormActions;
    isEmptyStateBannerDismissed: boolean;
    gridEntity?: BillGridEntity;
    selectedBills?: IBaseEntity[];
    lineItemsToInvoice?: ICheckedActionInvoiceData;
    voidPaymentModalData?: MassVoidAlertInfo;
    // Stashed when the void confirm modal opens, so the post-confirm toast (fired from a
    // separate handler that no longer has the selection) can pick a doc type, not a boolean.
    voidMainDocType?: BillDocumentType;
    sendToAccountingData?: CanSendEntitiesToAccountingResponse;
    updateAccountStatusData?: IBaseEntity[];
    showBulkOcrUploadModal: boolean;
    showEmailModal: boolean;
    showPayOnlineMarketingModal: boolean;
    shouldRenderAddUserToJobModal: boolean;
    shouldRenderPermissionWizardModal: boolean;
    subsAddedToJob: BTSelectItem[];
    internalUsersAddedToJob: BTSelectItem[];
    applyLienWaiverData?: ApplyLienWaiverResponse;
    billLineItemsToInvoice?: ILineItemsToInvoiceDetails;
    lineItemEntityMap?: Map<LineItemType, ILineItemEntityDetails>;
    upgradeRequiredError?: UpgradeRequiredError;
    showXeroAttachmentsBanner: boolean;
    filterDrawerOpen: boolean;
    selectedTab: TabsFilterTabs;
    defaultTab: TabsFilterTabs | null;
    /**
     * It happens when a tab filter has no overlap with a drawer filter
     */
    invalidFilters: boolean;
    uploadTrackerGroups: IUploadGroup[];
    tabCounts?: ITabCounts;
    /** True once the first full data load completes; keeps GridContainer mounted during subsequent reloads. */
    hasCompletedInitialLoad: boolean;
    /**
     * Pay-online permissions fetched separately when the deferBillsPermissionCalls flag is on.
     * Kept out of gridEntity because gridEntity is rebuilt on every grid reload (filter/paging/sort),
     * whereas these are only re-fetched on mount and job change.
     */
    deferredPayPermissions?: BillsGridPermissionsResponse;
    billViewMode: BillViewModePreference;
}

interface IAddUsersToJobProps extends RouteComponentProps {
    shouldRenderAddUserToJobModal: boolean;
    subsAddedToJob: BTSelectItem[];
    internalUsersAddedToJob: BTSelectItem[];
    jobIds: number[];
    handleBeforeClose: () => Promise<void>;
    handleShowPermissionWizard: () => void;
}

export enum BillExportFormat {
    Default = "default",
    Sage = "sage",
}

class AddUsersToJob extends Component<IAddUsersToJobProps> {
    render() {
        const { history, location, match } = this.props;
        return (
            <BTModal
                data-testid="btModalBillViewingPermisssion"
                title="Job Viewing Permission Wizard"
                width="700px"
                beforeClose={this.props.handleBeforeClose}
                removeBodyPadding
                visible={this.props.shouldRenderAddUserToJobModal}
            >
                <JobViewingPermissionWizard
                    jobId={this.props.jobIds[0]}
                    subs={[]}
                    internalUsers={[]}
                    autoAddedSubs={this.props.subsAddedToJob}
                    autoAddedInternalUsers={this.props.internalUsersAddedToJob}
                    openedFromEntity={OpenedFromEntity.Bills}
                    canSelectUsers={false}
                    onClose={this.props.handleShowPermissionWizard}
                    afterSubmit={this.props.handleShowPermissionWizard}
                    history={history}
                    location={location}
                    match={match}
                />
            </BTModal>
        );
    }
}

const CARD_LABELED_COLUMN_IDS = {
    DueDate: "12",
} as const;

const GRID_CARD_VIEW_CONFIG = {
    enabled: true,
    labeledColumnIds: [CARD_LABELED_COLUMN_IDS.DueDate],
} as const;

type FabActionId =
    | "sendForApproval"
    | "markReadyForPayment"
    | "approve"
    | "payOnline"
    | "recordOfflinePayment"
    | "addToInvoice"
    | "void"
    | "applyLienWaiver"
    | "printLienWaivers"
    | "createOwnerInvoice"
    | "sendToAccounting"
    | "updateAccountingStatus";

// Presentation-agnostic description of a resolved FAB action, rendered as either
// the inline primary button or an overflow ("More actions") menu item.
interface IFabActionConfig {
    testid: string;
    text: string;
    disabled: boolean;
    // May be async — BTFloatingActionBar fires the action without awaiting it.
    onClick: () => void | Promise<void>;
    /** Renders the action in the danger (red) style — e.g. "Void". */
    danger?: boolean;
}

// Buttons eligible for the FAB secondary slot. Order = display order in the overflow rollup.
// sendForApproval and approve are intentionally absent — they are primary-only on their assigned
// tab and never bleed into overflow on any other tab.
const OVERFLOW_ELIGIBLE: FabActionId[] = [
    "void",
    "applyLienWaiver",
    "printLienWaivers",
    "createOwnerInvoice",
    "addToInvoice",
    "recordOfflinePayment",
    "payOnline",
    "sendToAccounting",
    "updateAccountingStatus",
    "markReadyForPayment",
];

const getBulkActionBaseNoun = (mainDocType: BillDocumentType | undefined): string =>
    mainDocType === BillDocumentType.VendorCredit ? "Vendor Credit" : "Bill";

class BillListInternal extends Component<IBillListInternalProps, IBillListState> {
    private gridCanceller = new GridRequestCanceller();

    componentWillUnmount() {
        this.gridCanceller.cancel();
    }

    static defaultProps = {
        filterHandler: new FilterHandler(),
        listHandler: new BillListHandler(),
        emptyStateHandler: new EmptyStateHandler(),
        gridSettingsHandler: new GridSettingsHandler(),
        massBillPaymentHandler: new MassBillPaymentHandler(),
    };

    state: Readonly<IBillListState> = {
        gridState: initialGridState,
        actionBeingPerformed: undefined,
        isEmptyStateBannerDismissed: false,
        shouldRenderAddUserToJobModal: false,
        shouldRenderPermissionWizardModal: false,
        subsAddedToJob: [],
        internalUsersAddedToJob: [],
        billLineItemsToInvoice: undefined,
        showXeroAttachmentsBanner: false,
        showBulkOcrUploadModal: false,
        showEmailModal: false,
        showPayOnlineMarketingModal: false,
        filterDrawerOpen: false,
        selectedTab: TabsFilterTabs.inbox,
        defaultTab: (() => {
            const stored = BTLocalStorage.get("bt-string-billDefaultTab");
            return isTabsFilterTab(stored) ? stored : null;
        })(),
        invalidFilters: false,
        uploadTrackerGroups: [],
        hasCompletedInitialLoad: false,
        billViewMode: getBillViewModePreference(),
    };

    static contextType = BuilderInfoContext;
    declare context: React.ContextType<typeof BuilderInfoContext>;

    /**
     * Only the latest request is allowed to write to state, so overlapping fetches
     * can't apply a stale response over a newer one.
     */
    private gridPermissionsRequestId = 0;

    componentDidMount = () => {
        try {
            void this.handleReloadWithFilters(undefined, true);
            // Fired in parallel with the grid load; pay-online permissions don't block the grid render.
            void this.fetchGridPermissions();
        } catch (e) {
            if (e instanceof UpgradeRequiredError) {
                this.handleUpgradeRequiredError(e);
            } else {
                this.setState(() => {
                    throw e;
                });
            }
        }
    };

    componentDidUpdate = (prevProps: IBillListProps) => {
        if (!isEqual(prevProps.jobIds, this.props.jobIds)) {
            try {
                void this.handleReloadWithFilters(this.props.jobIds.length > 1);
                // Pay-online permissions vary by job, so re-fetch when the selected jobs change.
                void this.fetchGridPermissions();
            } catch (e) {
                if (e instanceof UpgradeRequiredError) {
                    this.handleUpgradeRequiredError(e);
                } else {
                    showAPIErrorMessage(e);
                }
            }
        }
        // In webview mode, refresh when pathname changes (e.g., back button from approval queue)
        else if (
            this.props.isWebview &&
            prevProps.location.pathname !== this.props.location.pathname
        ) {
            try {
                void this.handleReloadWithFilters();
            } catch (e) {
                if (e instanceof UpgradeRequiredError) {
                    this.handleUpgradeRequiredError(e);
                } else {
                    showAPIErrorMessage(e);
                }
            }
        }
        // External refresh signal (e.g. cascade-deleted bills navigating in from PO)
        else if (
            (this.props.location.state as { refresh?: number } | undefined)?.refresh !==
            (prevProps.location.state as { refresh?: number } | undefined)?.refresh
        ) {
            try {
                void this.handleReload();
            } catch (e) {
                if (e instanceof UpgradeRequiredError) {
                    this.handleUpgradeRequiredError(e);
                } else {
                    showAPIErrorMessage(e);
                }
            }
        }
    };

    private handleReloadWithFilters = async (
        refreshSavedViews?: boolean,
        isInitialLoad: boolean = false,
    ) => {
        try {
            this.setState((prevState) => ({
                gridState: gridStateManager.onDataActionPerformed(prevState.gridState),
            }));
            const { filterEntity, gridViewsResponse, ...updateData } =
                await getGridUpdateDataForFullReload(this.props, this.state, refreshSavedViews);

            if (
                filterEntity &&
                updateData.updatedFilterValues &&
                this.props.appliedFilters?.items &&
                getFilterEntityType(this.props.entityType) === this.props.appliedFilters.type
            ) {
                updateData.updatedFilterValues = applySerializeBehavior(filterEntity, {
                    items: this.props.appliedFilters.items,
                    savedFilter: updateData.updatedFilterValues.savedFilter,
                });
                this.setUnsavedAppliedFilter?.(this.props.appliedFilters.items, true);
            }

            this.applyInReviewFiltersFromRoute(filterEntity, updateData, refreshSavedViews);

            // Only honor the user's pinned default (or filter-derived tab) on a real
            // mount. Subsequent reloads (job change, webview pathname change, saved-view
            // application) must preserve the tab the user explicitly selected.
            const initialTab =
                isInitialLoad && this.props.isDraftBillsEffective && isInPortal({ builder: true })
                    ? this.getInitialTab(updateData.updatedFilterValues)
                    : undefined;

            this.setState({
                selectedTab: initialTab ?? this.state.selectedTab,
                filterEntity,
                gridViews: gridViewsResponse?.items,
                selectedGridView: gridViewsResponse?.defaultItem,
                gridEntity: {
                    ...defaultGridEntity,
                    columnsList: gridViewsResponse!.allGridColumns.filter((x) =>
                        gridViewsResponse?.defaultItem.columns.map((y) => y.id).includes(x.id),
                    ),
                    columnsListAll: gridViewsResponse!.allGridColumns,
                    canCreateOwnerInvoice: false,
                    canUpdateOwnerInvoice: false,
                    canVoid: false,
                    canPrintLienWaivers: false,
                    canUpdateAccountingStatus: false,
                    canSendToAccounting: false,
                    canMarkAsBillableToOwner: false,
                    canPayOnline: false,
                    showPayOnlineMarketing: false,
                    canPayInFull: false,
                    canMarkReadyForPayment: false,
                    isBillableToOwnerCheckedPreference: false,
                    invoiceableBillIds: [],
                    showAccountingReauthBanner: false,
                    accountingReauthBannerMessage: "",
                    accountingReauthBannerTitle: "",
                    billsToApproveCount: 0,
                    hasDraftOnlyAccess: false,
                },
                gridState: gridStateManager.onColumnsLoaded(
                    defaultGridState,
                    gridViewsResponse!.allGridColumns,
                ),
            });
            void this.fetchTabCounts(updateData.updatedFilterValues);
            const gridEntity = await this.loadList({
                ...updateData,
                updatedJobIds: this.props.jobIds,
            });
            if (!gridEntity) {
                this.setState((prevState) => ({
                    invalidFilters: true,
                    previousFilters: updateData.updatedFilterValues ?? prevState.previousFilters,
                    gridState: {
                        ...prevState.gridState,
                        dataState: "loaded",
                    },
                }));
                return;
            }

            this.setState((prevState) => ({
                invalidFilters: false,
                previousFilters: updateData.updatedFilterValues ?? prevState.previousFilters,
                gridState: gridStateManager.onDataAndColumnsLoaded(
                    prevState.gridState,
                    gridEntity.data,
                    gridEntity.columnsListAll,
                    gridEntity.footerData,
                    gridViewsResponse?.defaultItem.sort,
                    gridViewsResponse?.defaultItem,
                ),
                gridViews: gridViewsResponse?.items ?? prevState.gridViews,
                selectedGridView: gridViewsResponse?.defaultItem ?? prevState.selectedGridView,
                gridEntity: gridEntity,
                upgradeRequiredError: undefined,
                hasCompletedInitialLoad: true,
            }));
        } catch (e) {
            this.setState((prevState) => ({
                gridState: gridStateManager.onError(prevState.gridState),
            }));
            if (isGridErrorMessageApiError(e)) {
                showGridErrorMessage(e);
            } else {
                throw e;
            }
        }
    };

    private handleReload = async () => {
        void this.fetchTabCounts();
        await this.loadListIntoState({
            updatedPagingData: getDefaultPagingData(this.state.gridEntity?.pagingData, false),
        });
    };

    /**
     * On close of a details modal, we need to update the url
     * of the page back to the original route, and then reload
     * all data, starting from page 1
     */
    private handleBeforeClose = async () => {
        const { history, match } = this.props;
        this.setState({
            shouldRenderPermissionWizardModal: false,
        });
        history.replace(match.url);
        await this.handleReload();
    };

    private handleBillsCopySubmit = async (copyValues: ISelectedEntityCopyFormValues) => {
        await setLoadingAction<SelectedEntityCopyFormActions>(this, {
            actionBeingPerformed: "submit",
            callback: async () => {
                try {
                    const apiResponse = await this.props.listHandler!.copyBills(
                        this.props.builderId,
                        copyValues.selectedEntityIds,
                        copyValues.destinationJobId!,
                        copyValues.copyRelatedFiles!,
                        copyValues.newDeadLineDateTime,
                    );

                    if (
                        apiResponse.subsNotOnJobsite.length > 0 ||
                        apiResponse.internalUsersNotOnJobsite.length > 0
                    ) {
                        this.setState({
                            shouldRenderAddUserToJobModal: true,
                            subsAddedToJob: apiResponse.subsNotOnJobsite,
                            internalUsersAddedToJob: apiResponse.internalUsersNotOnJobsite,
                        });
                    } else {
                        void message.success(
                            getCopyBillsToastMessage(
                                apiResponse.exportedBillIds.length,
                                copyValues.selectedEntityIds.length,
                            ),
                            5,
                        );
                        void this.handleBeforeClose();
                    }
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
    };

    private handleMassActionCancel = async () => {
        const { history, match } = this.props;
        history.replace(match.url);
    };

    private handleShowPermissionWizard = () => {
        this.setState({
            shouldRenderAddUserToJobModal: false,
            shouldRenderPermissionWizardModal: true,
        });
    };

    private checkMassPayEligibility = async (
        selectedBills: IBaseEntity[],
        selectedJobId: number,
        isOnlinePayment: boolean,
    ) => {
        const paymentsResponse = await this.props.massBillPaymentHandler!.get(
            selectedBills,
            isOnlinePayment,
            selectedJobId,
        );

        let billIds = paymentsResponse.recipients.flatMap((x) =>
            x.relatedEntities.map((bill) => bill.id),
        );

        // don't allow invalid accounting bills to be sent to accounting immediately
        if (!isOnlinePayment && paymentsResponse.shouldSendToAccounting) {
            const accountingValidation =
                await this.props.massBillPaymentHandler!.getAccountingValidation(billIds);

            const accountingValidationMap = accountingValidation.entityValidationList.reduce(
                (map, val) => map.set(val.entityId, val.validationStatus),
                new Map<number, AccountingValidationStatus>(),
            );
            billIds = billIds.filter(
                (billId) =>
                    accountingValidationMap.get(billId) !== AccountingValidationStatus.Pending &&
                    accountingValidationMap.get(billId) !== AccountingValidationStatus.Invalid,
            );
        }

        const validBills = selectedBills.filter((bill) => billIds.includes(bill.id));
        const canMassPay = validBills.length !== 0;
        this.setState({ selectedBills: validBills });

        if (!canMassPay) {
            void message.warning("All selected Bills are ineligible for payment.", 5);
        } else {
            this.props.history.push(
                `${this.props.match.url}${routes.bill.getMassPayLink(isOnlinePayment)}`,
            );
        }
    };

    private fetchTabCounts = async (filterValues?: IFilterFormValues) => {
        if (!this.props.isDraftBillsEffective || !isInPortal({ builder: true })) {
            return;
        }
        try {
            const filters = getNullableFilterString(
                filterValues ?? this.state.previousFilters,
                true,
            ) as Record<string, string> | undefined;
            const response = await billsTabCountsPostV2({
                filters,
                jobIds: this.props.jobIds,
            });
            const counts = response.data;
            this.setState({
                tabCounts: {
                    draftBillsCount: counts.draftBillsCount ?? 0,
                    inReviewBillsCount: counts.inReviewBillsCount ?? 0,
                    readyForPaymentBillsCount: counts.readyForPaymentBillsCount ?? 0,
                    paidBillsCount: counts.paidBillsCount ?? 0,
                    otherBillsCount: counts.otherBillsCount ?? 0,
                },
            });
        } catch (error) {
            reportError(error);
        }
    };

    /**
     * Fetches the deferred pay-online permissions in parallel with the grid query. These depend only
     * on the user and selected jobs (not the grid content), so this only needs to run on mount and
     * when the selected jobs change.
     */
    private fetchGridPermissions = async () => {
        if (!this.context?.flags.deferBillsPermissionCalls) {
            return;
        }
        // Tag this request so a slower earlier fetch can't overwrite a newer one's result.
        const requestId = ++this.gridPermissionsRequestId;
        try {
            // Retry transient failure so permissions are available
            const response = await retryPolicy(
                () => billsGridPermissionsPostV2({ jobIds: this.props.jobIds }),
                {},
            );
            if (requestId === this.gridPermissionsRequestId) {
                this.setState({ deferredPayPermissions: response.data });
            }
        } catch (error) {
            reportError(error);
        }
    };

    /**
     * Resolves the pay-online permissions from the deferred call when the deferBillsPermissionCalls flag is on, otherwise from
     * the grid response (legacy inline path)
     */
    private getPayPermissions = (): {
        canPayOnline: boolean;
        showPayOnlineMarketing: boolean;
        canPayInFull: boolean;
    } => {
        const { gridEntity, deferredPayPermissions } = this.state;
        const source = this.context?.flags.deferBillsPermissionCalls
            ? deferredPayPermissions
            : gridEntity;
        return {
            canPayOnline: source?.canPayOnline ?? false,
            showPayOnlineMarketing: source?.showPayOnlineMarketing ?? false,
            canPayInFull: source?.canPayInFull ?? false,
        };
    };

    /**
     * Merges the tab status filter with the drawer status filter
     * If drawer filter is empty/all selected, use only the tab filter
     * If drawer filter has values, use the intersection of both filters
     */
    private mergeTabAndDrawerFilters = (
        drawerFilters: IFilterFormValues | undefined,
    ): IFilterFormValues | undefined => {
        const { filterEntity } = this.state;

        const tabFilters = this.getTabFilters();

        // If no drawer filters or no tab filter, return drawer filters as-is
        if (!drawerFilters || !filterEntity || tabFilters.length === 0) {
            return drawerFilters;
        }

        // Create a copy of drawer filters to avoid mutation
        const mergedFilters: IFilterFormValues = {
            ...drawerFilters,
            items: [...drawerFilters.items],
        };

        // Find the status filter in the drawer filters
        const statusFilterIndex = mergedFilters.items.findIndex(
            (filter) => String(filter.key) === String(BillFilterKey.Status),
        );

        if (statusFilterIndex === -1) {
            return drawerFilters;
        }

        const statusFilter = mergedFilters.items[statusFilterIndex];

        // Check if drawer status filter is empty or all selected
        const isDrawerFilterEmpty =
            statusFilter.allSelected ||
            !statusFilter.selectedValue ||
            (Array.isArray(statusFilter.selectedValue) && statusFilter.selectedValue.length === 0);

        if (isDrawerFilterEmpty) {
            // If drawer filter is empty, use only the tab filter
            mergedFilters.items[statusFilterIndex] = {
                ...statusFilter,
                selectedValue: tabFilters,
                allSelected: false,
            };
        } else {
            // If drawer filter has values, use the intersection of both filters
            const drawerStatusValues = statusFilter.selectedValue as BillStatusType[];
            const intersectionStatusValues = drawerStatusValues.filter((status) =>
                tabFilters.includes(status),
            );

            if (intersectionStatusValues.length === 0) return undefined;

            mergedFilters.items[statusFilterIndex] = {
                ...statusFilter,
                selectedValue: intersectionStatusValues,
                allSelected: false,
            };
        }

        return mergedFilters;
    };

    /**
     * This function abstracts the logic for actually fetching the list data. The parameters of
     * updatedFilterValues and updatedPagingData are only defined when they have been changed.
     * If filters haven't been changed, the api call is made with the previous filters from state.
     * If the paging data and the filters haven't changed, we can use the previous paging data
     * to make the request, if we have it.
     *
     * This method returns all of the list data, as well as
     * the filters used to make the request.
     */
    private loadList(gridUpdateData?: IGridUpdateData): Promise<BillGridEntity>;
    private async loadList(gridUpdateData: IGridUpdateData): Promise<BillGridEntity | undefined> {
        const signal = this.gridCanceller.getSignal();
        const { listHandler, jobIds, entityType } = this.props;
        const { updatedFilterValues, updatedPagingData, updatedSortData, updatedGridView } =
            gridUpdateData;
        const { previousFilters, gridEntity, gridState, selectedGridView } = this.state;
        const isSameGridView =
            updatedGridView && updatedGridView.viewId === selectedGridView?.viewId;
        let filters = updatedFilterValues || previousFilters;

        if (this.props.isDraftBillsEffective && isInPortal({ builder: true })) {
            // Merge tab filter with drawer filter before sending to API
            filters = this.mergeTabAndDrawerFilters(filters);
            if (!filters) return undefined;
        }

        const prevPagingData = gridEntity?.pagingData;
        let pagingData = updatedPagingData;
        if (
            !updatedFilterValues &&
            !pagingData &&
            prevPagingData &&
            (!updatedGridView || isSameGridView)
        ) {
            // Only pull the old paging data from state if we didn't apply new filters or if its the same grid view
            pagingData = prevPagingData;
        } else if (updatedGridView && !pagingData) {
            // initial load & full refreshes - used the saved page size if available, or pull from the grid view
            pagingData = new PagingData(getDefaultPageSize(entityType, updatedGridView.pageSize));
        }

        let sortingData = updatedSortData;
        if (!sortingData) {
            sortingData =
                updatedGridView && gridState.sortingData.length === 0
                    ? updatedGridView.sort
                    : gridState.sortingData;
        }
        // use column configs from state unless we are swapping to a different saved view or fully resetting the current view
        const columnConfigs =
            updatedGridView &&
            (updatedGridView.viewId !== selectedGridView?.viewId ||
                updatedGridView.shouldResetColumnConfigs)
                ? undefined
                : gridState.columnsList;

        const gridView = updatedGridView ?? selectedGridView;
        const gridRequest = mapGridStateToRequest(
            EmptyStateEntity.Bills,
            undefined,
            sortingData,
            gridView,
        );
        const BillGridResponse = await listHandler!.get(
            entityType,
            signal,
            gridRequest,
            jobIds,
            filters,
            pagingData,
        );

        BillGridResponse.data = BillGridResponse.data.map((bill) => {
            const billNumberLink = bill.billNumberLink;
            if (!billNumberLink) {
                return bill;
            }
            return {
                ...bill,
                billNumberLink: { ...billNumberLink, isDuplicate: bill.isDuplicated ?? false },
            };
        });
        const useAbsoluteLink = this.context?.flags.billsFullPageConversion ?? false;
        if (this.context?.flags.billsAgGridMigration) {
            // AG Grid path: map columns to AG cell renderers only. transformEntityAgGrid does NOT
            // run the Kendo per-column data mappers, so the AG renderers receive raw server values
            // and own their own mapping — no dependency on the Kendo render pipeline.
            BillGridResponse.transformEntityAgGrid(
                getBillAgColumnImplementations(useAbsoluteLink),
                gridView,
                columnConfigs,
            );
        } else {
            BillGridResponse.transformEntity(
                [...standardColumnImplementations, ...getBillColumnImplementations()],
                gridView,
                columnConfigs,
            );
        }
        return BillGridResponse;
    }

    /**
     * This function abstracts the state management associated with loading all the grid data.
     * Filter changes and paging changes will flow through this method.
     */
    private loadListIntoState = async (gridUpdateData: IGridUpdateData) => {
        try {
            const effectiveFilters =
                gridUpdateData.updatedFilterValues ?? this.state.previousFilters;
            const updatedColumns = gridUpdateData.updatedGridView
                ? filterSelectedGridColumns(
                      this.state.gridEntity!.columnsListAll,
                      gridUpdateData.updatedGridView.columns,
                  )
                : null;
            this.setState((prevState) => ({
                previousFilters: gridUpdateData.updatedFilterValues
                    ? gridUpdateData.updatedFilterValues
                    : prevState.previousFilters,
                selectedGridView: gridUpdateData.updatedGridView
                    ? gridUpdateData.updatedGridView
                    : prevState.selectedGridView,
                // Mark loading so the grid shows its overlay while the reload is in flight
                // (sort, paging, filters, view changes), cleared when the data resolves below.
                gridState: gridStateManager.onDataActionPerformed(
                    gridStateManager.onColumnsLoaded(
                        prevState.gridState,
                        updatedColumns
                            ? updatedColumns.filteredColumns
                            : prevState.gridState.columnsList!,
                    ),
                ),
                gridEntity: {
                    ...prevState.gridEntity!,
                    columnsList: updatedColumns
                        ? updatedColumns.filteredColumns
                        : prevState.gridEntity!.columnsList,
                    columnsListAll: updatedColumns
                        ? updatedColumns.updatedColumnsList
                        : prevState.gridEntity!.columnsListAll,
                },
            }));
            if (gridUpdateData.updatedFilterValues) {
                void this.fetchTabCounts(effectiveFilters);
            }
            const gridEntity = await this.loadList(gridUpdateData);
            if (!gridEntity) {
                this.setState({ invalidFilters: true });
                return true;
            }

            this.setState((prevState) => ({
                invalidFilters: false,
                gridState: getUpdatedGridState(prevState, gridUpdateData, gridEntity),
                gridEntity: gridEntity,
                upgradeRequiredError: undefined,
            }));
            return true;
        } catch (e) {
            if (isCancelledGridRequestError(e)) return false;
            if (e instanceof UpgradeRequiredError) {
                this.handleUpgradeRequiredError(e);
            } else {
                showGridErrorMessage(e);
                this.setState((prevState) => ({
                    gridState: gridStateManager.onError(prevState.gridState),
                }));
            }
            return false;
        }
    };

    private handleUpgradeRequiredError = (error: UpgradeRequiredError) => {
        this.setState(
            (prevState) => ({
                upgradeRequiredError: error,
                gridState: gridStateManager.onError(prevState.gridState),
            }),
            () => scrollToPosition({ PosX: 0, PosY: 0 }),
        );
    };

    private handleUpsellBeforeClose = async () => {
        const { history, match } = this.props;
        history.replace(match.url);

        try {
            await this.handleReloadWithFilters(true);
        } catch (e) {
            if (e instanceof UpgradeRequiredError) {
                this.handleUpgradeRequiredError(e);
            } else {
                showGridErrorMessage(e);
                this.setState((prevState) => ({
                    gridState: gridStateManager.onError(prevState.gridState),
                }));
            }
        }
    };

    private getTabFilters = () => {
        let tabStatusFilter: BillStatusType[];
        switch (this.state.selectedTab) {
            case TabsFilterTabs.inbox:
                tabStatusFilter = [BillStatusType.Draft];
                break;
            case TabsFilterTabs.inReview:
                tabStatusFilter = [BillStatusType.Open, BillStatusType.InReview];
                break;
            case TabsFilterTabs.readyForPayment:
                tabStatusFilter = [
                    BillStatusType.ReadyForPayment,
                    BillStatusType.PaidHoldingRetainage,
                ];
                break;
            case TabsFilterTabs.paid:
                tabStatusFilter = [
                    BillStatusType.UnderSubReview,
                    BillStatusType.OnlinePaymentProcessing,
                    BillStatusType.Paid,
                ];
                break;
            case TabsFilterTabs.allBills:
                tabStatusFilter = [
                    BillStatusType.Open,
                    BillStatusType.ReadyForPayment,
                    BillStatusType.Paid,
                    BillStatusType.PaidHoldingRetainage,
                    BillStatusType.Void,
                    BillStatusType.UnderSubReview,
                    BillStatusType.OnlinePaymentProcessing,
                    BillStatusType.OnlinePaymentDeclined,
                    BillStatusType.OnlinePaymentFailed,
                    BillStatusType.InReview,
                    BillStatusType.Requested,
                ] as BillStatusType[];
                break;
            default:
                tabStatusFilter = [];
                break;
        }
        return tabStatusFilter;
    };

    private getInitialTab = (filters: IFilterFormValues | undefined): TabsFilterTabs => {
        // Honor the user's pinned default when nothing in the URL is forcing a specific tab.
        const userDefault = this.state.defaultTab;

        if (!filters) return userDefault ?? TabsFilterTabs.inbox;

        const statusFilter = filters.items.find(
            (filter) => String(filter.key) === String(BillFilterKey.Status),
        );

        const isFilterEmpty =
            !statusFilter ||
            statusFilter.allSelected ||
            !statusFilter.selectedValue ||
            (Array.isArray(statusFilter.selectedValue) && statusFilter.selectedValue.length === 0);

        if (isFilterEmpty) return userDefault ?? TabsFilterTabs.inbox;

        const drawerStatuses = statusFilter.selectedValue as BillStatusType[];

        if (drawerStatuses.includes(BillStatusType.Draft)) {
            return TabsFilterTabs.inbox;
        }

        return TabsFilterTabs.allBills;
    };

    private getTabCounts = (): ITabCounts | undefined => {
        const counts = this.state.tabCounts;

        if (!counts) {
            return undefined;
        }

        const drawerStatuses = this.getActiveDrawerStatuses();

        // No drawer status filter → show all counts
        if (drawerStatuses === null) {
            return counts;
        }

        // Drawer status filter active → only show count if tab's statuses are included in filter
        const isTabIncluded = (tabStatuses: BillStatusType[]): boolean =>
            tabStatuses.some((s) => drawerStatuses.includes(s));

        return {
            draftBillsCount: isTabIncluded([BillStatusType.Draft]) ? counts.draftBillsCount : 0,
            inReviewBillsCount: isTabIncluded([BillStatusType.Open, BillStatusType.InReview])
                ? counts.inReviewBillsCount
                : 0,
            readyForPaymentBillsCount: isTabIncluded([
                BillStatusType.ReadyForPayment,
                BillStatusType.PaidHoldingRetainage,
            ])
                ? counts.readyForPaymentBillsCount
                : 0,
            paidBillsCount: isTabIncluded([
                BillStatusType.UnderSubReview,
                BillStatusType.OnlinePaymentProcessing,
                BillStatusType.Paid,
            ])
                ? counts.paidBillsCount
                : 0,
            otherBillsCount: isTabIncluded([
                BillStatusType.Void,
                BillStatusType.OnlinePaymentDeclined,
                BillStatusType.OnlinePaymentFailed,
            ])
                ? counts.otherBillsCount
                : 0,
        };
    };

    private getActiveDrawerStatuses = (): BillStatusType[] | null => {
        const filterCount = this.getFilterCount();
        if (filterCount === 0) {
            return null;
        }

        const { previousFilters } = this.state;
        if (!previousFilters) {
            return null;
        }

        const statusFilter = previousFilters.items.find(
            (filter) => String(filter.key) === String(BillFilterKey.Status),
        );

        if (!statusFilter || statusFilter.allSelected) {
            return null;
        }

        const selected = statusFilter.selectedValue as BillStatusType[] | undefined;
        if (!Array.isArray(selected) || selected.length === 0) {
            return null;
        }

        return selected;
    };

    private handleTabSelected = (selectedTab: TabsFilterTabs) => {
        this.setState({ selectedTab }, () => {
            // Reload the list with the new tab filter
            void this.loadListIntoState({
                updatedPagingData: this.state.gridEntity?.pagingData,
            });
        });
    };

    private handleDefaultTabChanged = (defaultTab: TabsFilterTabs | null) => {
        BTLocalStorage.set("bt-string-billDefaultTab", defaultTab);
        this.setState({ defaultTab });
    };

    private static readonly allowedDropExtensions = new Set(ALLOWED_BILL_UPLOAD_FILE_TYPES);

    private handleFilesDropped = (files: File[]) => {
        const validFiles = files.filter((f) => {
            const ext = f.name.split(".").pop()?.toLowerCase();
            return ext && BillListInternal.allowedDropExtensions.has(ext);
        });

        if (validFiles.length === 0) {
            const types = ALLOWED_BILL_UPLOAD_FILE_TYPES.map((t) => `.${t}`).join(", ");
            void message.error(`No supported files detected. Allowed types: ${types}`);
            return;
        }

        this.setState((prev) => ({
            uploadTrackerGroups: [
                ...prev.uploadTrackerGroups,
                {
                    groupId: `drop-${Date.now()}`,
                    pendingFiles: validFiles,
                    fileNames: validFiles.map((f) => f.name),
                    builderId: this.props.builderId,
                },
            ],
        }));
    };

    /**
     * This function is called when filters are updated by the user,
     * and will reload all of the data in the grid
     */
    private handleFilterSubmit = async (
        updatedFilterValues: IFilterFormValues,
        actionBeingPerformed: FilterFormActions,
        hideToast?: boolean,
    ) => {
        await setLoadingAction<FilterFormActions>(this, {
            actionBeingPerformed,
            callback: async () => {
                const success = await this.loadListIntoState({
                    updatedFilterValues,
                    updatedPagingData: this.state.gridEntity?.pagingData,
                });
                if (success && !hideToast) {
                    void message.success("Results updated");
                }
            },
        });
    };

    /**
     * This function is called when the current page or size is changed,
     * and will reload all of the data in the grid
     */
    private handlePagingChange = async (updatedPagingData: PagingData) => {
        await this.loadListIntoState({ updatedPagingData });
    };

    private handleSort = async (updatedSortData: SortDescriptor[]) => {
        await this.loadListIntoState({ updatedSortData });
    };

    private handleViewApplied = async (updatedGridView: GridViewItem) => {
        await this.loadListIntoState({ updatedGridView, updatedSortData: updatedGridView.sort });
        void message.success("View applied");
    };

    private handleSaveView = async (
        values: IGridSettingsFormValues,
        columns: GridViewColumn[],
        saveAsNew: boolean,
    ) => {
        try {
            const { gridSettingsHandler, entityType } = this.props;
            const { gridEntity, gridState, selectedGridView } = this.state;
            const request = mapSavedViewToRequest(
                values,
                columns,
                gridEntity!.pagingData,
                entityType,
                gridEntity!.columnsListAll,
                gridState.sortingData,
            );
            const savedViewResponse = saveAsNew
                ? await gridSettingsHandler!.createView(request)
                : await gridSettingsHandler!.updateView(values.savedView, request);
            const gridViewsResponse = await gridSettingsHandler!.getGridViews(entityType);
            const savingFromBasePage = this.props.settingsState === "closed";
            const shouldUpdateGrid =
                savingFromBasePage ||
                (selectedGridView && selectedGridView.viewId === values.savedView);
            this.setState({
                gridViews: gridViewsResponse.items,
            });
            this.props.setSettingsState(savingFromBasePage ? "closed" : "open");
            if (shouldUpdateGrid) {
                await this.loadListIntoState({ updatedGridView: savedViewResponse.view });
            }
            if (savingFromBasePage) {
                void message.success("View saved");
            }
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleDeleteView = async (view: GridViewItem) => {
        try {
            const { gridSettingsHandler, entityType } = this.props;
            await gridSettingsHandler!.deleteView(view.viewId);
            const gridViewsResponse = await gridSettingsHandler!.getGridViews(entityType);
            this.setState({ gridViews: gridViewsResponse.items });
            await this.loadListIntoState({ updatedGridView: gridViewsResponse.defaultItem });
            void message.success("View deleted");
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleColumnReorder = (columnOrder: string[]) => {
        this.setState((prevState) => ({
            gridState: gridStateManager.onColumnReorder(prevState.gridState, columnOrder),
        }));
    };
    private handleColumnResize = (columnKey: string, width: number) => {
        this.setState((prevState) => ({
            gridState: gridStateManager.onColumnResize(prevState.gridState, columnKey, width),
        }));
    };

    private handleColumnFrozen = (columnId: string, isFrozen: boolean) => {
        this.setState((prevState) => ({
            gridState: gridStateManager.onColumnFreeze(prevState.gridState, columnId, isFrozen),
        }));
    };

    /**
     * AG Grid column persistence: BillListAgGrid emits an updater for a resize / reorder / freeze
     * (matched by column id). Apply it against the latest columnsList inside the functional
     * setState so two AG events firing before a re-render can't clobber each other by both closing
     * over the same stale render snapshot. Flag unsaved so the saved-views toolbar's Save/Reset
     * light up — reusing the same gridState the footer reads.
     */
    private handleAgColumnsListChange = (
        updater: (current: GridColumn<unknown>[]) => GridColumn<unknown>[],
    ) => {
        this.setState((prevState) => ({
            gridState: {
                ...prevState.gridState,
                columnsList: updater(prevState.gridState.columnsList ?? []),
                hasUnsavedChanges: true,
            },
        }));
    };

    private handleDismissEmptyStateBanner = async () => {
        try {
            await this.props.emptyStateHandler!.dismissAlert(EmptyStateEntity.Bills);
            this.setState({ isEmptyStateBannerDismissed: true });
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleDownloadClicked = async (
        columnsList?: GridColumn<unknown, unknown>[],
        sortingData?: SortDescriptor[],
    ) => {
        const { listHandler, jobIds } = this.props;
        const { previousFilters, gridEntity } = this.state;
        const columns = columnsList?.map((col) => col.id);
        // Match the grid's filtering: fold the selected tab's status filter into the drawer
        // filters so the export reflects the visible list, not the full All Bills set.
        let filters = previousFilters;
        if (this.props.isDraftBillsEffective && isInPortal({ builder: true })) {
            filters = this.mergeTabAndDrawerFilters(filters);
            if (!filters) return;
        }
        try {
            await setLoadingAction<ToolbarDownloadAction>(this, {
                actionBeingPerformed: "download",
                callback: async () => {
                    const gridRequest = mapGridStateToRequest(
                        EmptyStateEntity.Bills,
                        columns,
                        sortingData,
                        undefined,
                        gridEntity?.footerData,
                    );
                    const response = await listHandler!.download(jobIds, gridRequest, filters);
                    triggerBrowserDownload(response.blobData, response.fileName);
                },
            });
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleExportModalDownload = async (format: BillExportFormat) => {
        const { gridState } = this.state;

        if (format === BillExportFormat.Default) {
            await this.handleDownloadClicked(gridState.columnsList, gridState.sortingData);
        } else {
            await this.handleExportToAccounting();
        }
    };

    private handleExportToAccounting = async () => {
        const { jobIds, isAllJobsSelected } = this.props;
        const { previousFilters, gridEntity, gridState } = this.state;
        const columns = gridState.columnsList?.map((col) => col.id);

        // Match the grid's filtering: fold the selected tab's status filter into the drawer
        // filters so the export reflects the visible list, not the full All Bills set.
        let mergedFilters = previousFilters;
        if (this.props.isDraftBillsEffective && isInPortal({ builder: true })) {
            mergedFilters = this.mergeTabAndDrawerFilters(mergedFilters);
            if (!mergedFilters) return;
        }

        try {
            await setLoadingAction<ToolbarDownloadAction>(this, {
                actionBeingPerformed: "download",
                callback: async () => {
                    const gridRequest = mapGridStateToRequest(
                        EmptyStateEntity.Bills,
                        columns,
                        gridState.sortingData,
                        undefined,
                        gridEntity?.footerData,
                    );

                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    const filters = getNullableFilterString(mergedFilters, true);
                    const response = await billsExportToAccountingPostV2(
                        {
                            gridRequest,
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            filters,
                            jobIds,
                            isAllJobsSelected,
                        },
                        {
                            integrationType: IntegrationTypes.SageIntacct,
                        },
                    );

                    const filename: string = getFileNameFromAxiosResponse(
                        response,
                        "bills_sage_export.csv",
                    );
                    triggerBrowserDownload(response.data, filename);
                },
            });
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleViewChanged = async (field: string, value: number) => {
        const { gridViews, selectedGridView } = this.state;
        const selectedView = gridViews!.find((view) => view.value === value);
        if (
            selectedView &&
            selectedView.extraData &&
            selectedView.value !== selectedGridView!.viewId
        ) {
            await this.handleViewApplied(selectedView.extraData);
        }
    };

    private handleResetView = async () => {
        const { gridViews, selectedGridView } = this.state;
        const savedView = gridViews!.find((view) => parseInt(view.id) === selectedGridView?.viewId);
        if (savedView && savedView.extraData) {
            await this.handleViewApplied({
                ...savedView?.extraData,
                shouldResetColumnConfigs: true,
            });
        }
    };

    private handleAddNewSavedView = async () => {
        this.props.setSettingsState("addingCurrent");
    };

    private handleSetGridSettingState = (state: SavedViewsAndFiltersSettingsState) => {
        this.props.setSettingsState(state);
    };

    private handleCreateOwnerInvoiceClicked = async (
        selectedIds: IBaseEntity[],
        builderId: number,
        selectedJobId: number,
    ) => {
        if (selectedIds.length > 100) {
            void message.warn(
                "Only a max of 100 bills can be selected at a time to create invoice. If you need an invoice to include more than 100 bills, please do so in batches of 100 bills or less.",
                7,
            );
            return;
        }

        try {
            const response = await this.props.listHandler!.getBillLineItemIds(
                selectedIds.map((p) => p.id),
                selectedJobId,
            );

            if (response.lineItemIds.length > 0) {
                this.setState({
                    lineItemsToInvoice: mapResponseToInvoiceData(
                        response,
                        LineItemType.Bill,
                        builderId,
                        selectedJobId,
                    ),
                });
                this.props.history.push(`${this.props.match.url}${routes.lineItemsToInvoice.path}`);
            } else {
                void message.info("No eligible bills are selected.");
            }
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleAddToExistingInvoiceClick = (selectedBillIds: IBaseEntity[]) => {
        if (selectedBillIds.length > 100) {
            void message.warn(
                "Only a max of 100 bills can be selected at a time to add to an invoice. If you need an invoice to include more than 100 bills, please do so in batches of 100 bills or less.",
                7,
            );
            return;
        }

        this.props.history.push(this.props.match.url + routes.existingOwnerInvoiceSelect.path);
    };

    private handleMarkReadyForPaymentClicked = async (selectedBills: IBillRowSelection[]) => {
        try {
            await setLoadingAction<BillListFormActions>(this, {
                actionBeingPerformed: "checkedAction",
                callback: async () => {
                    const { totalPaymentCount, successfulCount } =
                        await this.props.listHandler!.markReadyForPayment(
                            selectedBills.map((p) => p.id),
                        );
                    bulkActionToast(
                        successfulCount,
                        totalPaymentCount,
                        getBillBulkSummary("markedReady", getSelectionDocType(selectedBills)),
                    );
                },
            });
        } catch (e) {
            showAPIErrorMessage(e);
        }
        await this.handleCheckedActionClose();
    };

    private handleSendForApprovalClicked = async (selectedBills: IBillRowSelection[]) => {
        try {
            await setLoadingAction<BillListFormActions>(this, {
                actionBeingPerformed: "checkedAction",
                callback: async () => {
                    const response = await billsMassSendForApprovalPutV2({
                        billIds: selectedBills.map((b) => b.id),
                    });
                    const updatedCount = response.data.updatedCount ?? 0;
                    const totalCount = response.data.totalCount ?? 0;
                    bulkActionToast(
                        updatedCount,
                        totalCount,
                        getBillBulkSummary("sentForApproval", getSelectionDocType(selectedBills)),
                    );
                },
            });
        } catch (e) {
            showAPIErrorMessage(e);
        }
        await this.handleCheckedActionClose();
    };

    private handleApproveClicked = async (selectedBills: IBillRowSelection[]) => {
        await setLoadingAction<BillListFormActions>(this, {
            actionBeingPerformed: "checkedAction",
            callback: async () => {
                await performBulkApprove(selectedBills);
            },
        });
        await this.handleCheckedActionClose();
    };

    private handleVoidCheckedActionClicked = async (selectedBills: IBillRowSelection[]) => {
        try {
            await setLoadingAction<BillListFormActions>(this, {
                actionBeingPerformed: "checkedAction",
                callback: async () => {
                    // Derived here and stashed in state: void routes through a confirmation modal,
                    // so the doc type must survive until handleVoidActionClicked fires the toast.
                    const mainDocType = getSelectionDocType(selectedBills);
                    const response = await this.props.listHandler!.getMassVoidAlertInfo(
                        selectedBills.map((p) => p.id),
                    );

                    if (response.paymentIdsToVoid.length === 0) {
                        void message.warn(getBillBulkSummary("voidNoneEligible", mainDocType));
                    } else {
                        this.setState({
                            voidPaymentModalData: response,
                            voidMainDocType: mainDocType,
                        });
                    }
                },
            });
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleVoidActionClicked = async (
        isDeleteFromAccountingChecked?: boolean,
        relatedBillIdsToVoid?: number[],
    ) => {
        try {
            await setLoadingAction<PaymentVoidConfirmFormActions>(this, {
                actionBeingPerformed: "void",
                callback: async () => {
                    let billIdsToVoid = this.state.voidPaymentModalData!.paymentIdsToVoid;
                    if (relatedBillIdsToVoid) {
                        billIdsToVoid = [...billIdsToVoid, ...relatedBillIdsToVoid];
                    }

                    const { voidedPaymentCount, totalPaymentCount } =
                        await this.props.listHandler!.voidSelectedPayments(
                            billIdsToVoid,
                            isDeleteFromAccountingChecked,
                        );
                    // Capture before handleCheckedActionClose resets voidMainDocType to undefined.
                    const voidMainDocType = this.state.voidMainDocType;
                    await this.handleCheckedActionClose();

                    bulkActionToast(
                        voidedPaymentCount,
                        totalPaymentCount,
                        getBillBulkSummary("voided", voidMainDocType),
                    );
                },
            });
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleUpdateAccountingStatusClicked = async (selectedIds: IBaseEntity[]) => {
        this.setState({
            updateAccountStatusData: selectedIds,
        });
    };

    private handleUpdateAccountingStatus = async () => {
        await setLoadingAction<BillListFormActions>(this, {
            actionBeingPerformed: "checkedAction",
            callback: async () => {
                try {
                    const paymentIds = this.state.updateAccountStatusData ?? [];
                    const { totalPaymentCount, successfulSyncCount } =
                        await this.props.listHandler!.updateAccountingStatus(
                            paymentIds.map((p) => p.id),
                        );
                    if (successfulSyncCount === totalPaymentCount) {
                        void message.success("Updated");
                    } else {
                        btError({
                            title: "Payments were not updated",
                            content: `Buildertrend was unable to connect to ${this.state.gridEntity?.accountingIntegrationName}. Refresh to try again.`,
                        });
                    }
                } catch (e) {
                    showAPIErrorMessage(e);
                }
                await this.handleCheckedActionClose();
            },
        });
    };

    private handleCanSendToAccounting = async (selectedIds: IBaseEntity[]) => {
        try {
            await setLoadingAction<BillListFormActions>(this, {
                actionBeingPerformed: "checkedAction",
                callback: async () => {
                    const resp = await this.props.listHandler!.canSendToAccounting(
                        selectedIds.map((p) => p.id),
                    );
                    if (resp.validIds.length === 0) {
                        btError({
                            content: `None of the selected items can be sent to ${this.state.gridEntity?.accountingIntegrationName}. Please modify your selection and try again.`,
                        });
                    } else {
                        this.setState({
                            sendToAccountingData: resp,
                        });
                    }
                },
            });
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleBulkDelete = async (
        selectedBills: IBillRowSelection[],
        accountingIntegration: AccountingIntegrationType,
        accountingIntegrationName: string,
        canSendToAccounting?: boolean,
    ) => {
        let title = "";
        let theseOrThis = "this";
        const mainDocType = getSelectionDocType(selectedBills);
        const baseNoun = getBulkActionBaseNoun(mainDocType);
        let entityNameToDisplay = baseNoun;

        if (selectedBills.length > 1) {
            theseOrThis = "these";
            title = `Delete ${selectedBills.length} ${baseNoun}s?`;
            entityNameToDisplay = `${baseNoun}s`;
        } else {
            title = `Delete ${baseNoun}?`;
        }
        const content =
            accountingIntegration && canSendToAccounting
                ? `Deleting ${theseOrThis} ${entityNameToDisplay} will not delete any associated ${entityNameToDisplay} in ${accountingIntegrationName}.`
                : `Are you sure you want to delete ${theseOrThis} ${entityNameToDisplay}?`;
        btDeleteConfirm({
            title: title,
            content: content,
            onOk: async () => {
                await setLoadingAction<BillListFormActions>(this, {
                    actionBeingPerformed: "checkedAction",
                    callback: async () => {
                        try {
                            const response = await this.props.listHandler!.deleteBills({
                                selectedIds: selectedBills,
                            });
                            void this.handleBeforeClose();
                            const deleteSummary = getBillBulkSummary("deleted", mainDocType);
                            if (response.successCount === response.count) {
                                void message.success(
                                    `${response.successCount} of ${response.count} ${deleteSummary}`,
                                );
                            } else {
                                const messageStr = (
                                    <>
                                        {response.successCount} of {response.count} {deleteSummary}
                                        <br />
                                        <br />
                                        {baseNoun}s with online payments in "Online Payment
                                        Processing" or "Paid" statuses cannot be deleted.
                                    </>
                                );
                                void message.error(messageStr, 5);
                            }
                            deleteSearchEntity(
                                SearchCategory.Bills,
                                ...selectedBills.map((e) => e.id),
                            );
                        } catch (e) {
                            showAPIErrorMessage(e);
                        }
                    },
                });
            },
        });
    };

    private handleSendToAccounting = async (
        billIds: number[],
        markBillableToOwner: boolean,
        invoiceDate: moment.Moment | undefined,
    ) => {
        return await this.props.listHandler!.sendToAccounting(
            billIds,
            markBillableToOwner,
            invoiceDate,
        );
    };

    private handleAddNewBill = () => {
        const { history, match, jobIds } = this.props;
        const jobId = jobIds.length === 1 ? jobIds[0] : 0;
        history.push(match.url + routes.bill.getDetailsPath(0, jobId));
    };

    private handleApplyLienWaiverClicked = async (selectedIds: IBaseEntity[]) => {
        try {
            await setLoadingAction<BillListFormActions>(this, {
                actionBeingPerformed: "checkedAction",
                callback: async () => {
                    const response =
                        await this.props.listHandler!.getApplyLienWaiverInfo(selectedIds);

                    if (response.eligibleBills.length === 0) {
                        void message.warn("No eligible bills are selected");
                    } else {
                        this.setState({
                            applyLienWaiverData: response,
                        });
                    }
                },
            });
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    /**
     * Clear all data used for checked actions and reload
     */
    private handleCheckedActionClose = async () => {
        this.setState({
            lineItemsToInvoice: undefined,
            voidPaymentModalData: undefined,
            voidMainDocType: undefined,
            sendToAccountingData: undefined,
            updateAccountStatusData: undefined,
            applyLienWaiverData: undefined,
        });
        await this.handleBeforeClose();
    };

    private clearLineItemsToInvoice = () => {
        this.setState({
            lineItemsToInvoice: undefined,
        });
    };

    private handleSetDefaultView = async (view: GridViewItem) => {
        try {
            const { gridSettingsHandler, entityType } = this.props;
            await gridSettingsHandler?.setDefaultView!(view.viewId, entityType);
            void message.success(`${view.viewName} was set as your default view`);
            const gridViewsResponse = await gridSettingsHandler!.getGridViews(entityType);
            this.setState({
                gridViews: gridViewsResponse.items,
            });
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleImportBillsFromTemplate = async () => {
        const { history, match } = this.props;
        await this.handleReload();
        history.replace(match.url);
    };

    private setUnsavedAppliedFilter = (values: SelectedFilterItem[], isChanged: boolean) => {
        this.props.filterReducerDispatch(new SetUnsavedAppliedFilter(values, isChanged));
    };

    private updateUnsavedChanges = (
        newFilters: SelectedFilterItem[],
        filterEntity: FilterEntity,
        savedFilter: SavedFilterItem,
        applyFilterItems = true,
    ): void => {
        this.props.filterReducerDispatch(
            new UpdateUnsavedChanges(newFilters, filterEntity, savedFilter, applyFilterItems),
        );
    };

    private getFilterCount = (): number => {
        const { filterReducerState } = this.props;
        const { filterEntity } = this.state;

        if (!filterReducerState.appliedFilter || !filterEntity) {
            return 0;
        }

        return getAppliedFilterCount(filterReducerState.appliedFilter, filterEntity);
    };

    // Applies InReview status, current user Approver, and Awaiting Approval filters when accessed via InReview route.
    // Only runs on initial load, not when refreshing saved views.
    private applyInReviewFiltersFromRoute = (
        filterEntity: FilterEntity | undefined,
        updateData: { updatedFilterValues?: IFilterFormValues },
        refreshSavedViews?: boolean,
    ): void => {
        if (!filterEntity || refreshSavedViews || !updateData.updatedFilterValues) {
            return;
        }

        const { view } = this.props.match.params;

        if (view !== BillListRouteView.InReview || !this.props.userInfo?.globalUserId) {
            return;
        }

        const filterItems = getSelectedFilterItemsFromSavedFilter(
            filterEntity,
            updateData.updatedFilterValues.savedFilter,
        );

        const statusFilter = filterItems.find((filter) => filter.key === BillFilterKey.Status);
        if (statusFilter) {
            statusFilter.selectedValue = [BillStatusType.InReview];
            statusFilter.allSelected = false;
        }

        const approverFilter = filterItems.find((filter) => filter.key === BillFilterKey.Approver);
        if (approverFilter) {
            approverFilter.selectedValue = [this.props.userInfo.globalUserId];
            approverFilter.allSelected = false;
        }

        const awaitingApprovalFilter = filterItems.find(
            (filter) => filter.key === BillFilterKey.AwaitingApprovalOnly,
        );
        if (awaitingApprovalFilter) {
            awaitingApprovalFilter.selectedValue = true;
            awaitingApprovalFilter.allSelected = false;
        }

        const serializedForApi = applySerializeBehavior(filterEntity, {
            items: filterItems,
            savedFilter: updateData.updatedFilterValues.savedFilter,
        });

        updateData.updatedFilterValues = serializedForApi;

        this.updateUnsavedChanges(
            filterItems,
            filterEntity,
            updateData.updatedFilterValues.savedFilter,
            true,
        );
    };

    private handleSetLineItemsToInvoice = (
        lineItemsToInvoiceDetails?: ILineItemsToInvoiceDetails,
    ) => {
        this.setState({
            billLineItemsToInvoice: lineItemsToInvoiceDetails,
        });
    };

    private getActionBar() {
        const { gridEntity, gridState } = this.state;
        const { jobIds, history, match, isXsScreen, isDraftBillsEffective, isWebview } = this.props;
        const isBuilder = isInPortal({ builder: true });
        const jobId = jobIds.length === 1 ? jobIds[0] : 0;

        const filterCount = this.getFilterCount();

        const helpCenterButton = (
            <BdsRollupButton
                maxStage={1}
                id="bills-list-page-header-help-content-header-button"
                testid="bills-list-page-header-help-content-header-button"
                className="listpageheader-icon-hide"
                icon={<BdsIconHelp />}
                displayType="tertiary"
                value="Help"
                text="Help"
                ariaLabel="Help"
                tooltip="Go to Help Center"
                onClick={() => {
                    window.open(
                        "https://helpcenter.buildertrend.net/s/article/Bills-Overview",
                        "_blank",
                        "noopener,noreferrer",
                    );
                }}
            />
        );

        const oldExportButton = !this.context?.flags.billsAccountingExport &&
            gridEntity &&
            !isWebview &&
            gridEntity.records > 0 &&
            isBuilder && (
                <BdsRollupButton
                    maxStage={1}
                    id="bills-list-page-header-export-content-header-button"
                    testid="bills-list-page-header-export-content-header-button"
                    className="listpageheader-icon-hide"
                    icon={<BdsIconExport />}
                    displayType="tertiary"
                    value="Export"
                    text="Export"
                    ariaLabel="Export"
                    tooltip="Export all"
                    onClick={async () => {
                        !this.context?.flags.billsAccountingExport &&
                            (await this.handleDownloadClicked(
                                gridState.columnsList,
                                gridState.sortingData,
                            ));
                    }}
                />
            );

        const exportButton = this.context?.flags.billsAccountingExport &&
            gridEntity &&
            gridEntity.records > 0 &&
            !isWebview &&
            isBuilder && (
                <BdsRollupButton
                    id="billsListExportGroup"
                    testid="billsListExportGroup"
                    maxStage={1}
                    text=""
                    icon={<BdsIconExport />}
                    value="billsListExportGroup"
                    displayType="tertiary"
                >
                    <BdsRollupButton
                        displayType="secondary"
                        id="billsListExport"
                        testid="billsListExport"
                        text="Export"
                        value="billsListExport"
                        icon={<BdsIconExport />}
                        onClick={() => this.handleExportModalDownload(BillExportFormat.Default)}
                    />
                    <BdsRollupButton
                        displayType="secondary"
                        id="billsListExportSageIntacct"
                        testid="billsListExportSageIntacct"
                        text="Export for Sage Intacct"
                        value="billsListExportSageIntacct"
                        icon={<BdsIconExport />}
                        onClick={() => this.handleExportModalDownload(BillExportFormat.Sage)}
                    />
                </BdsRollupButton>
            );

        const filterButton = (
            <BdsListPageHeaderFilterButton
                maxStage={1}
                minStage={1}
                id="bills-list-page-header-filter-content-header-button"
                testid="bills-list-page-header-filter-content-header-button"
                filterCount={filterCount}
                className="listpageheader-icon-hide"
                icon={<BdsIconFunnel />}
                displayType="tertiary"
                value="Filter"
                text="Filter"
                ariaLabel="Filter"
                tooltip="Filter"
                onClick={() =>
                    this.setState({
                        filterDrawerOpen: !this.state.filterDrawerOpen,
                    })
                }
            />
        );

        const emailToInboxButton = gridEntity?.canAdd &&
            isDraftBillsEffective &&
            isInPortal({ builder: true }) && (
                <BdsRollupButton
                    id="emailBillsAndReceipts"
                    testid="emailBillsAndReceipts"
                    text="Email to Inbox"
                    value="emailBillsAndReceipts"
                    displayType="secondary"
                    minStage={1}
                    maxStage={isXsScreen ? 1 : 2}
                    icon={isXsScreen ? <BdsIconEnvelopeSimple /> : undefined}
                    onClick={() => this.setState({ showEmailModal: true })}
                />
            );

        const oldNewBillButton = gridEntity?.canAdd && (
            <BdsButtonGroup
                flexAlign="end"
                id="bills-list-page-header-split-buttongroup"
                testid="bills-list-page-header-split-buttongroup"
                menuTestid="bills-list-page-header-split-buttongroup-menu"
                rollupTestid="bills-list-page-header-split-buttongroup-rollup"
                splitButton
            >
                <BdsRollupButton
                    displayType="primary"
                    id="newBill"
                    testid="newBill"
                    minStage={2}
                    text="Bill"
                    ariaLabel="Create new Bill"
                    value="newBill"
                    icon={<BdsIconPlus />}
                    onClick={() =>
                        history.push(
                            this.state.billViewMode === "fullPage"
                                ? routes.bill.getAddPageLink(jobId)
                                : match.url + routes.bill.getDetailsLink(0, jobId),
                        )
                    }
                />
                <BdsRollupButton
                    id="bills-list-page-header-rolled-primary-buttons"
                    testid="bills-list-page-header-rolled-primary-buttons"
                    displayType="primary"
                    value="More create options"
                    ariaLabel="More create options"
                >
                    {isDraftBillsEffective && (
                        <BdsRollupButton
                            id="bulkUploadBills"
                            testid="bulkUploadBills"
                            text="Bulk upload"
                            value="bulkUpload"
                            maxStage={0}
                            onClick={() => this.setState({ showBulkOcrUploadModal: true })}
                        />
                    )}
                    <BdsRollupButton
                        id="importBills"
                        testid="importBills"
                        text="Import from template"
                        value="importBills"
                        maxStage={0}
                        onClick={() =>
                            history.push(
                                match.url +
                                    routes.templateImport.getDetailsLink(
                                        TabPermissions.PurchaseOrders,
                                    ),
                            )
                        }
                    />
                    {this.context?.flags?.vendorCreditRelease1 && (
                        <BdsRollupButton
                            id="newVendorCredit"
                            testid="newVendorCredit"
                            text="Add Vendor Credit"
                            value="newVendorCredit"
                            maxStage={0}
                            onClick={() =>
                                history.push(
                                    match.url +
                                        routes.bill.getDetailsLink(0, jobId) +
                                        "?documentType=vendorCredit",
                                )
                            }
                        />
                    )}
                </BdsRollupButton>
            </BdsButtonGroup>
        );

        if (isDraftBillsEffective) {
            return (
                <BdsActionBar testid="bills-list-page-header-actionbar">
                    <BdsButtonGroup
                        id="bills-list-page-header-buttongroup"
                        testid="bills-list-page-header-buttongroup"
                        menuTestid="bills-list-page-header-buttongroup-menu"
                        rollupTestid="bills-list-page-header-buttongroup-rollup"
                        displayType="tertiary"
                        grow
                        flexAlign="end"
                    >
                        {helpCenterButton}
                        {oldExportButton}
                        {exportButton}
                        {filterButton}
                        {emailToInboxButton}
                    </BdsButtonGroup>
                    {oldNewBillButton}
                </BdsActionBar>
            );
        }

        return (
            <BdsActionBar testid="bills-list-page-header-actionbar">
                <BdsButtonGroup
                    id="bills-list-page-header-buttongroup"
                    testid="bills-list-page-header-buttongroup"
                    menuTestid="bills-list-page-header-buttongroup-menu"
                    rollupTestid="bills-list-page-header-buttongroup-rollup"
                    displayType="tertiary"
                    grow
                    flexAlign="end"
                >
                    {helpCenterButton}
                    {oldExportButton}
                    {exportButton}
                    {filterButton}
                    {emailToInboxButton}
                </BdsButtonGroup>

                {oldNewBillButton}
            </BdsActionBar>
        );
    }

    private getBTHeaderContent() {
        const { gridEntity, gridState } = this.state;
        const { jobIds, history, match, isXsScreen, isDraftBillsEffective, isWebview, jobName } =
            this.props;
        const isBuilder = isInPortal({ builder: true });
        const jobId = jobIds.length === 1 ? jobIds[0] : 0;
        const filterCount = this.getFilterCount();
        const billsAccountingExport = !!this.context?.flags.billsAccountingExport;

        const showExport = !!(gridEntity && gridEntity.records > 0 && !isWebview && isBuilder);

        const iconActions = [
            ...(billsAccountingExport
                ? []
                : [
                      {
                          icon: "export" as const,
                          onClick: async () => {
                              await this.handleDownloadClicked(
                                  gridState.columnsList,
                                  gridState.sortingData,
                              );
                          },
                          isHidden: !showExport,
                      },
                  ]),
            {
                icon: "help" as const,
                onClick: () => {
                    window.open(
                        "https://helpcenter.buildertrend.net/s/article/Bills-Overview",
                        "_blank",
                        "noopener,noreferrer",
                    );
                },
            },
            { icon: "filter" as const, onClick: () => this.setState({ filterDrawerOpen: true }) },
        ];

        const secondaryActions: React.ReactNode[] = [];
        if (billsAccountingExport && showExport) {
            secondaryActions.push(
                <BTDropdownButton
                    key="billsListExportGroup"
                    data-testid="billsListExportGroup"
                    type="tertiary"
                    menuItems={[
                        {
                            "data-testid": "billsListExport",
                            content: "Export",
                            onClick: () => this.handleExportModalDownload(BillExportFormat.Default),
                        },
                        {
                            "data-testid": "billsListExportSageIntacct",
                            content: "Export for Sage Intacct",
                            onClick: () => this.handleExportModalDownload(BillExportFormat.Sage),
                        },
                    ]}
                >
                    {isXsScreen ? "Export" : <BdsIconExport />}
                </BTDropdownButton>,
            );
        }
        if (gridEntity?.canAdd && isDraftBillsEffective && isInPortal({ builder: true })) {
            secondaryActions.push(
                <BTButton
                    key="emailBillsAndReceipts"
                    data-testid="emailBillsAndReceipts"
                    type="secondary"
                    onClick={() => this.setState({ showEmailModal: true })}
                >
                    Email to Inbox
                </BTButton>,
            );
        }

        const menuItems: BTSplitButtonItem[] = [];
        if (isDraftBillsEffective) {
            menuItems.push({
                content: "Bulk upload",
                onClick: () => this.setState({ showBulkOcrUploadModal: true }),
                "data-testid": "bulkUploadBills",
            });
        }
        menuItems.push({
            content: "Import from template",
            onClick: () =>
                history.push(
                    match.url + routes.templateImport.getDetailsLink(TabPermissions.PurchaseOrders),
                ),
            "data-testid": "importBills",
        });
        if (this.context?.flags?.vendorCreditRelease1) {
            menuItems.push({
                content: "Add Vendor Credit",
                onClick: () =>
                    history.push(
                        match.url +
                            routes.bill.getDetailsLink(0, jobId) +
                            "?documentType=vendorCredit",
                    ),
                "data-testid": "newVendorCredit",
            });
        }

        const tabsAndActionCard = (
            <div className="flex flex-row gap-2">
                {!isWebview && (
                    <PurchaseOrderTab
                        {...this.props}
                        id="billsListTabs"
                        activeTab={PurchaseOrderTabTypes.Bill}
                    />
                )}
                {(gridEntity?.billsToApproveCount ?? 0) > 0 &&
                    this.props.match.params.view !== BillListRouteView.InReview &&
                    !this.props.isTemplateMode && (
                        <ActionCard
                            id="billApprovalCard"
                            testid="billApprovalCard"
                            icon={<BdsIconBills />}
                            actionTextOne="Bill needs your approval"
                            actionTextOther="Bills need your approval"
                            colorScheme="warning"
                            count={gridEntity!.billsToApproveCount}
                            onClick={() => {
                                dispatchTracking({
                                    event: "LinkClick",
                                    component: "Bill Approval Action Card",
                                    uniqueId: "bill-approval-quick-filter",
                                });
                                if (this.props.isWebview && this.props.webviewContext) {
                                    history.push(
                                        routes.bill.getWebviewApprovalQueueLink(
                                            this.props.webviewContext,
                                        ),
                                    );
                                } else {
                                    history.push(routes.bill.getApprovalQueueLink());
                                }
                            }}
                        />
                    )}
            </div>
        );

        return (
            <BTListHeader
                testid="bills-list-page-header"
                className="bills-list-page-header"
                ariaLabel="Bills"
            >
                <div className="flex flex-col gap-2">
                    <div>
                        <BTListHeader.ActionButtons
                            testid="bills-list-page-header-action-buttons"
                            jobName={jobName}
                            filterCount={filterCount}
                            iconActions={iconActions}
                            secondaryActions={
                                secondaryActions.length > 0 ? secondaryActions : undefined
                            }
                            primaryAction={
                                <>
                                    {gridEntity?.canAdd && (
                                        <BTSplitButton
                                            collapse
                                            data-testid="primaryActionButton"
                                            type="primary"
                                            buttonAction={{
                                                content: "Bill",
                                                onClick: () =>
                                                    history.push(
                                                        this.state.billViewMode === "fullPage"
                                                            ? routes.bill.getAddPageLink(jobId)
                                                            : match.url +
                                                                  routes.bill.getDetailsLink(
                                                                      0,
                                                                      jobId,
                                                                  ),
                                                    ),
                                                icon: <BdsIconPlus />,
                                                "data-testid": "newBill",
                                                "aria-label": "Create new Bill",
                                                hotkey: "new",
                                            }}
                                            menuItems={menuItems}
                                        />
                                    )}
                                </>
                            }
                        />
                        <BTListHeader.Title>Bills</BTListHeader.Title>
                    </div>
                    {tabsAndActionCard}
                </div>
            </BTListHeader>
        );
    }

    private getContentHeader() {
        const { gridEntity } = this.state;
        const { jobName, history } = this.props;
        const useBTHeader = this.context?.flags?.bdsContentHeaderListPageReplacement;

        const accountingConnectionRoute =
            routes.settings.getSettingsLink() + routes.accounting.getSettingsLink();

        return (
            <>
                {gridEntity?.showAccountingReauthBanner && (
                    <BTAlert
                        data-testid="reauthBanner"
                        type="info"
                        title={gridEntity?.accountingReauthBannerTitle}
                        className="margin-top-sm"
                        message={
                            <div className="AccountingBannerMessage">
                                <li>{gridEntity?.accountingReauthBannerMessage}</li>
                                <BTLink
                                    shouldOpenNewTab
                                    useAutoSPARouting
                                    to={accountingConnectionRoute}
                                >
                                    <BTButton data-testid="reauthConnection" type="primary">
                                        Update Connection
                                    </BTButton>
                                </BTLink>
                            </div>
                        }
                        closable
                        showIcon
                    />
                )}

                {useBTHeader ? (
                    this.getBTHeaderContent()
                ) : (
                    <BdsContentHeader
                        id="bills-list-page-header"
                        testid="bills-list-page-header"
                        className="bills-list-page-header"
                        jobName={jobName}
                        title="Bills"
                        slot={this.getActionBar()}
                        actionBar={
                            <BdsActionBar id="billsListActionBar" testid="billsListActionBar">
                                {!this.props.isWebview && (
                                    <PurchaseOrderTab
                                        {...this.props}
                                        id="billsListTabs"
                                        activeTab={PurchaseOrderTabTypes.Bill}
                                    />
                                )}
                                {(gridEntity?.billsToApproveCount ?? 0) > 0 &&
                                    this.props.match.params.view !== BillListRouteView.InReview &&
                                    !this.props.isTemplateMode && (
                                        <>
                                            <ActionCard
                                                id="billApprovalCard"
                                                testid="billApprovalCard"
                                                icon={<BdsIconBills />}
                                                actionTextOne="Bill needs your approval"
                                                actionTextOther="Bills need your approval"
                                                colorScheme="warning"
                                                count={gridEntity!.billsToApproveCount}
                                                onClick={() => {
                                                    dispatchTracking({
                                                        event: "LinkClick",
                                                        component: "Bill Approval Action Card",
                                                        uniqueId: "bill-approval-quick-filter",
                                                    });
                                                    if (
                                                        this.props.isWebview &&
                                                        this.props.webviewContext
                                                    ) {
                                                        // Use the proper route helper to build the webview URL
                                                        history.push(
                                                            routes.bill.getWebviewApprovalQueueLink(
                                                                this.props.webviewContext,
                                                            ),
                                                        );
                                                    } else {
                                                        history.push(
                                                            routes.bill.getApprovalQueueLink(),
                                                        );
                                                    }
                                                }}
                                            />
                                        </>
                                    )}
                            </BdsActionBar>
                        }
                    />
                )}
            </>
        );
    }

    private getPrimaryActionIds = (selectedTab: TabsFilterTabs): FabActionId[] => {
        const { canPayOnline, showPayOnlineMarketing } = this.getPayPermissions();
        const showPayOnlinePrimary = canPayOnline || showPayOnlineMarketing;
        const payOrRecord: FabActionId = showPayOnlinePrimary
            ? "payOnline"
            : "recordOfflinePayment";

        if (!this.props.isDraftBillsEffective) {
            return ["markReadyForPayment"];
        }

        switch (selectedTab) {
            case TabsFilterTabs.inbox:
                return ["sendForApproval"];
            case TabsFilterTabs.inReview:
                return ["approve"];
            case TabsFilterTabs.readyForPayment:
                return [payOrRecord];
            case TabsFilterTabs.paid:
                return ["addToInvoice"];
            case TabsFilterTabs.allBills:
                return ["markReadyForPayment"];
            default:
                return [];
        }
    };

    // Resolves a single FAB action into a presentation-agnostic config, or null
    // when the current permissions/selection hide it entirely. The same config
    // drives both the inline primary button (BTFloatingActionBar.Action) and the
    // overflow menu items (BTFloatingActionBar.MoreActionsItem).
    private getFabActionConfig = (
        id: FabActionId,
        selectedBills: IBillRowSelection[],
    ): IFabActionConfig | null => {
        const { gridEntity, actionBeingPerformed } = this.state;
        const { jobIds, builderId } = this.props;
        const jobId = jobIds.length === 1 ? jobIds[0] : 0;
        const selectedIds = selectedBills.map((b) => b.id);
        const isBuilder = isInPortal({ builder: true });
        const disabled = actionBeingPerformed !== undefined || selectedBills.length === 0;
        const hasInvoiceableBills =
            selectedIds.length !== 0 &&
            !!gridEntity?.invoiceableBillIds?.some((x) => selectedIds.includes(x));
        const { canPayOnline, showPayOnlineMarketing, canPayInFull } = this.getPayPermissions();
        const showPayOnline = (canPayOnline || showPayOnlineMarketing) && isBuilder;

        switch (id) {
            case "sendForApproval":
                if (!gridEntity?.canUpdate) return null;
                return {
                    testid: "sendForApproval",
                    text: "Send for approval",
                    disabled,
                    onClick: () => this.handleSendForApprovalClicked(selectedBills),
                };

            case "markReadyForPayment": {
                if (!gridEntity?.canMarkReadyForPayment) return null;
                const noneEligible = selectedBills.every(
                    (b) => !canMarkReadyForPayment(b.purchaseOrderPaymentStatus),
                );
                return {
                    testid: "markReadyForPayment",
                    text: "Mark ready for payment",
                    disabled: disabled || noneEligible,
                    onClick: () => this.handleMarkReadyForPaymentClicked(selectedBills),
                };
            }

            case "approve":
                if (!gridEntity?.canUpdate) return null;
                return {
                    testid: "approve",
                    text: "Approve",
                    disabled,
                    onClick: () => this.handleApproveClicked(selectedBills),
                };

            case "payOnline":
                if (!showPayOnline || this.props.isWebview) return null;
                return {
                    testid: "payOnline",
                    text: "Pay",
                    disabled,
                    onClick: async () => {
                        if (showPayOnlineMarketing && !canPayOnline) {
                            this.setState({ showPayOnlineMarketingModal: true });
                            return;
                        }
                        await this.checkMassPayEligibility(selectedBills, jobId, true);
                    },
                };

            case "recordOfflinePayment": {
                if (!canPayInFull || !isBuilder) return null;
                const noneEligible = selectedBills.every(
                    (b) => !canRecordOfflinePayment(b.purchaseOrderPaymentStatus),
                );
                return {
                    testid: "payInFull",
                    text: "Record payment",
                    disabled: disabled || noneEligible,
                    onClick: async () => {
                        await this.checkMassPayEligibility(selectedBills, jobId, false);
                    },
                };
            }

            case "addToInvoice":
                if (!gridEntity?.canUpdateOwnerInvoice) return null;
                return {
                    testid: "updateOwnerInvoice",
                    text: "Add to existing invoice",
                    disabled: disabled || jobId === JobIdTypes.AllJobs || !hasInvoiceableBills,
                    onClick: () => this.handleAddToExistingInvoiceClick(selectedBills),
                };

            case "void":
                if (!gridEntity?.canVoid || !isBuilder) return null;
                return {
                    testid: "voidPayments",
                    text: "Void",
                    disabled,
                    danger: true,
                    onClick: () => this.handleVoidCheckedActionClicked(selectedBills),
                };

            case "applyLienWaiver": {
                // Vendor credits have no lien waiver UI, so they are excluded here: the option is
                // hidden when the selection is only vendor credits, and eligibility is evaluated on
                // the bills in the selection so a mix of vendor credits and ineligible bills disables
                // (rather than hides) the option.
                const billsInSelection = selectedBills.filter(
                    (b) => !isVendorCredit(b.purchaseOrderPaymentStatus),
                );
                const allVendorCredits = billsInSelection.length === 0;
                const noneEligible = billsInSelection.every(
                    (b) => !canApplyLienWaiver(b.purchaseOrderPaymentStatus),
                );
                if (!gridEntity?.canUpdate || !isBuilder || allVendorCredits) return null;
                return {
                    testid: "applyLienWaiver",
                    text: "Apply lien waivers",
                    disabled: disabled || noneEligible,
                    onClick: async () => await this.handleApplyLienWaiverClicked(billsInSelection),
                };
            }

            case "printLienWaivers":
                if (!gridEntity?.canPrintLienWaivers) return null;
                return {
                    testid: "lienWaiverPrint",
                    text: "Print applied lien waivers",
                    disabled,
                    onClick: () => {
                        window.open(
                            routes.appBase + routes.lienWaiver.getPrintLink(selectedIds),
                            "_blank",
                            "noopener,noreferrer",
                        );
                    },
                };

            case "createOwnerInvoice":
                if (!gridEntity?.canCreateOwnerInvoice) return null;
                return {
                    testid: "createOwnerInvoice",
                    text: "New invoice",
                    disabled: disabled || jobId === JobIdTypes.AllJobs,
                    onClick: () =>
                        this.handleCreateOwnerInvoiceClicked(selectedBills, builderId, jobId),
                };

            case "sendToAccounting":
                if (!gridEntity?.canSendToAccounting) return null;
                return {
                    testid: "sendToAccounting",
                    text: `Send to ${gridEntity.accountingIntegrationName}`,
                    disabled,
                    onClick: () => this.handleCanSendToAccounting(selectedBills),
                };

            case "updateAccountingStatus":
                if (!gridEntity?.canUpdateAccountingStatus) return null;
                return {
                    testid: "updateAccountingStatus",
                    text: `Update ${gridEntity.accountingIntegrationName} status`,
                    disabled,
                    onClick: () => this.handleUpdateAccountingStatusClicked(selectedBills),
                };
        }
    };

    private renderFab = (
        onDeselect: () => void,
        selectedCount: number,
        selectedBills: IBaseEntity[],
    ) => {
        const { jobIds, history, match } = this.props;
        const { gridEntity, gridState, selectedTab } = this.state;
        const jobId = jobIds.length === 1 ? jobIds[0] : 0;

        // Grid trims selectedBills to {id, builderId, jobId}; re-hydrate from gridState.data
        // so predicates below can read fields like purchaseOrderPaymentStatus.
        const rowsById = new Map<number, IBillRowSelection>(
            (gridState.data ?? []).map((row) => {
                const billRow = row as IBillRowSelection;
                return [billRow.id, billRow];
            }),
        );
        const billRows: IBillRowSelection[] = selectedBills.map(
            (b) => rowsById.get(b.id) ?? (b as IBillRowSelection),
        );

        const selectedIds = billRows.map((b) => b.id);

        const primaryIds = this.getPrimaryActionIds(selectedTab);
        const primarySet = new Set<FabActionId>(primaryIds);
        const secondaryIds = OVERFLOW_ELIGIBLE.filter((id) => !primarySet.has(id));

        // The one inline primary action for the current tab (if any), plus the
        // overflow ("More actions") items — both resolved through the same config
        // so permissions/disabled logic stays in one place.
        const primaryConfig = primaryIds
            .map((id) => this.getFabActionConfig(id, billRows))
            .find((config): config is IFabActionConfig => config !== null);
        const secondaryConfigs = secondaryIds
            .map((id) => this.getFabActionConfig(id, billRows))
            .filter((config): config is IFabActionConfig => config !== null);

        return (
            <BTFloatingActionBar
                testid="bills-floating-action-bar"
                selectedCount={selectedCount}
                onDeselect={onDeselect}
            >
                {!this.props.isWebview && (
                    <BTFloatingActionBar.IconAction
                        id="print"
                        icon={<IconPrinter />}
                        onClick={() => {
                            void window.open(
                                routes.appBase + routes.bill.getPrintLink(selectedIds, jobId),
                                "_blank",
                                "noopener,noreferrer",
                            );
                        }}
                    >
                        Print
                    </BTFloatingActionBar.IconAction>
                )}
                {/* no jobId guard — server resolves each bill's source job */}
                {gridEntity?.canAdd && (
                    <BTFloatingActionBar.IconAction
                        id="copy"
                        icon={<IconCopy />}
                        onClick={() =>
                            history.push(match.url + routes.list.getCopyLink(selectedIds))
                        }
                    >
                        Copy
                    </BTFloatingActionBar.IconAction>
                )}
                {gridEntity?.canDelete && (
                    <BTFloatingActionBar.IconAction
                        id="delete"
                        icon={<IconTrash />}
                        danger
                        onClick={() =>
                            this.handleBulkDelete(
                                billRows,
                                gridEntity?.accountingIntegrationType!,
                                gridEntity?.accountingIntegrationName!,
                                gridEntity?.canSendToAccounting,
                            )
                        }
                    >
                        Delete
                    </BTFloatingActionBar.IconAction>
                )}

                {primaryConfig && (
                    <>
                        <BTFloatingActionBar.Divider />
                        <BTFloatingActionBar.Action
                            id={primaryConfig.testid}
                            disabled={primaryConfig.disabled}
                            danger={primaryConfig.danger}
                            onClick={primaryConfig.onClick}
                        >
                            {primaryConfig.text}
                        </BTFloatingActionBar.Action>
                    </>
                )}

                {secondaryConfigs.length > 0 && (
                    <>
                        <BTFloatingActionBar.Divider />
                        <BTFloatingActionBar.MoreActions>
                            {secondaryConfigs.map((config) => (
                                <BTFloatingActionBar.MoreActionsItem
                                    key={config.testid}
                                    id={config.testid}
                                    disabled={config.disabled}
                                    danger={config.danger}
                                    onClick={config.onClick}
                                >
                                    {config.text}
                                </BTFloatingActionBar.MoreActionsItem>
                            ))}
                        </BTFloatingActionBar.MoreActions>
                    </>
                )}
            </BTFloatingActionBar>
        );
    };

    private getFooter = () => {
        const { gridEntity, gridState, gridViews, selectedGridView } = this.state;
        const { entityType } = this.props;

        return (
            <ListPageFooter withListActionBar id="BillBdsListFooterSection">
                <ListPageFooter.Layout
                    testid="BillBdsListFooterSection"
                    className="BillListFooterSection"
                    viewControls={
                        gridViews && gridState && selectedGridView ? (
                            <ShowOnPortal
                                builder
                                render={() => (
                                    <ManageSavedViews
                                        gridViews={gridViews}
                                        selectedGridView={selectedGridView}
                                        columnsList={gridState.columnsList}
                                        hasUnsavedChanges={gridState.hasUnsavedChanges}
                                        onViewChanged={this.handleViewChanged}
                                        setGridSettingsState={this.handleSetGridSettingState}
                                        onSaveView={this.handleSaveView}
                                        onAddNewSavedView={this.handleAddNewSavedView}
                                        onResetView={this.handleResetView}
                                        gridCardViewEnabled={
                                            !!this.context?.flags.mobileWebviewExperience
                                        }
                                        {...(this.context?.flags.billsAgGridMigration && {
                                            getPopupContainer: getClosestModal,
                                        })}
                                    />
                                )}
                            />
                        ) : undefined
                    }
                    paginationControls={
                        gridEntity?.pagingData ? (
                            <BTPagination
                                entityType={entityType}
                                pagingData={gridEntity.pagingData}
                                onChange={this.handlePagingChange}
                                id="BillPagingBottom"
                            />
                        ) : undefined
                    }
                />
            </ListPageFooter>
        );
    };

    /**
     * Renders the appropriate empty state for the grid based on current state:
     * - If invalidFilters: shows "No results" message
     * - If no data: shows standard empty state
     * - If has data: returns null (grid will show data)
     */
    private renderGridEmptyState = () => {
        const { gridEntity, invalidFilters, selectedTab } = this.state;

        if (!gridEntity) return null;

        if (invalidFilters || !gridEntity?.listMetadata.hasData) {
            const isInboxEmptyStateAudience =
                selectedTab === TabsFilterTabs.inbox &&
                !invalidFilters &&
                !!this.props.isDraftBillsEffective &&
                isInPortal({ builder: true });

            if (isInboxEmptyStateAudience) {
                return (
                    <BTListLayoutWhitespace>
                        <BillDraftsEmptyState
                            onUpload={() => this.setState({ showBulkOcrUploadModal: true })}
                        />
                    </BTListLayoutWhitespace>
                );
            }

            return (
                <BTListLayoutWhitespace>
                    <BillEmptyState
                        hasListData={false}
                        hasFilteredData={invalidFilters}
                        isReadonly={gridEntity.listMetadata.emptyStatesMetadata.isReadOnly}
                        onCallToActionClick={this.handleAddNewBill}
                        helpLink={gridEntity.listMetadata.emptyStatesMetadata.helpLink}
                    />
                </BTListLayoutWhitespace>
            );
        } else {
            return null;
        }
    };

    render() {
        const { entityType, gridSettingsHandler, jobIds, match, filterReducerState, jobName } =
            this.props;

        const {
            filterEntity,
            gridState,
            actionBeingPerformed,
            isEmptyStateBannerDismissed,
            gridViews,
            selectedGridView,
            gridEntity,
            lineItemsToInvoice,
            voidPaymentModalData,
            sendToAccountingData,
            updateAccountStatusData,
            upgradeRequiredError,
            applyLienWaiverData,
        } = this.state;

        const isBuilder = isInPortal({ builder: true });
        const jobId = jobIds.length === 1 ? jobIds[0] : JobIdTypes.AllJobs;
        const isInboxDropzoneActive =
            this.state.selectedTab === TabsFilterTabs.inbox &&
            !!this.props.isDraftBillsEffective &&
            isBuilder &&
            !this.props.isWebview &&
            !this.props.isXsScreen &&
            !!gridEntity?.canAdd;

        const modalConfig = {
            parentRoute: match.url,
            beforeClose: this.handleBeforeClose,
        };

        if (upgradeRequiredError) {
            return (
                <PackageUpsellList
                    upsellTitle="Bills"
                    imageSrc="/images/Upsell/purchaseOrders.svg"
                    listTitle="Bills"
                    upgradeBeforeClose={this.handleUpsellBeforeClose}
                    upgradeRequiredInfo={upgradeRequiredError}
                    isLoading={gridState.dataState === "loading"}
                >
                    <Space direction="vertical" size="large">
                        Experience easy-to-use functionality for your PO processing.
                        <ul>
                            <li>
                                Have your subs sign off on scope of work and payment terms online
                            </li>
                            <li>Track approvals, PO status and invoices</li>
                            <li>Import from software such as SoftPlan and Excel</li>
                        </ul>
                    </Space>
                </PackageUpsellList>
            );
        }

        return (
            <BillLinkAbsoluteContext.Provider value={this.state.billViewMode === "fullPage"}>
                <BTListLayoutBDS
                    title="Bills"
                    data-testid="billsList"
                    className="BillListLayout"
                    jobName={jobName}
                    renderWithActions={(renderListActions) => {
                        // Shared checked-action toolbar content, used by both the Kendo
                        // GridContainer (via toolbarContentConfig) and the AG Grid branch.
                        const renderBillToolbarContent = (
                            selectedBills: IBaseEntity[],
                        ): React.ReactNode => {
                            const selectedTypedBills = selectedBills as IBillRowSelection[];
                            const builderId =
                                selectedTypedBills.length === 0
                                    ? 0
                                    : selectedTypedBills[0].builderId;
                            const selectedIds = selectedTypedBills.map((b) => b.id);
                            const routeInvoice = (
                                <RouteExistingOwnerInvoiceSelect
                                    jobId={jobId}
                                    builderId={builderId}
                                    entityType={LineItemType.Bill}
                                    beforeClose={this.handleBeforeClose}
                                    entityIds={selectedIds}
                                    parentRoute={this.props.match.url}
                                    canIncludeAttachments
                                    setLineItemsToInvoice={this.handleSetLineItemsToInvoice}
                                />
                            );
                            return (
                                <>
                                    {routeInvoice}
                                    {renderListActions(this.getContentHeader())}
                                </>
                            );
                        };

                        return (
                            <>
                                <RoutePurchaseOrder modalConfig={modalConfig} />
                                <BillPaymentRoute
                                    selectedPayables={this.state.selectedBills!}
                                    modalConfig={modalConfig}
                                />
                                <RouteTemplateImport
                                    modalConfig={modalConfig}
                                    onSubmit={this.handleImportBillsFromTemplate}
                                    jobId={jobId}
                                />
                                <RouteBill modalConfig={modalConfig} />
                                <RouteOwnerInvoice
                                    modalConfig={modalConfig}
                                    lineItemsToInvoice={this.state.billLineItemsToInvoice}
                                    onSave={this.handleSetLineItemsToInvoice}
                                />
                                <RouteConversation modalConfig={modalConfig} />
                                <RouteSelectedEntityCopy
                                    modalConfig={modalConfig}
                                    entityType={ListEntityType.Bills}
                                    labelForCopyRelatedFiles="Copy Bill Documents"
                                    onSubmit={this.handleBillsCopySubmit}
                                    onCancel={this.handleMassActionCancel}
                                    showCopyRelatedFiles
                                />
                                {lineItemsToInvoice && (
                                    <RouteLineItemsToInvoice
                                        {...this.props}
                                        {...lineItemsToInvoice}
                                        handler={this.props.invoiceHandler}
                                        entityType={LineItemType.Bill}
                                        beforeClose={this.clearLineItemsToInvoice}
                                        parentRoute={this.props.match.url}
                                        setLineItemsToInvoice={this.handleSetLineItemsToInvoice}
                                        jobName={jobName}
                                        accountingIntegrationName={
                                            gridEntity?.accountingIntegrationName
                                        }
                                    />
                                )}
                                {voidPaymentModalData && (
                                    <PaymentVoidConfirm
                                        onOk={this.handleVoidActionClicked}
                                        onCancel={this.handleCheckedActionClose}
                                        visible
                                        payment={voidPaymentModalData}
                                        actionBeingPerformed={
                                            actionBeingPerformed as PaymentVoidConfirmFormActions
                                        }
                                        isMassVoid
                                    />
                                )}
                                <RouteMessageCompose
                                    modalConfig={{
                                        parentRoute: match.url,
                                        beforeClose: this.handleCheckedActionClose,
                                    }}
                                />
                                {sendToAccountingData && (
                                    <SendToAccountingBulkAction
                                        validIds={sendToAccountingData.validIds}
                                        invalidIds={sendToAccountingData.invalidIds}
                                        integrationName={gridEntity?.accountingIntegrationName}
                                        accountingTermForPrimaryOrSecondaryJobEntity={
                                            sendToAccountingData.accountingTermForPrimaryOrSecondaryJobEntity
                                        }
                                        canMarkAsBillableToOwner={
                                            gridEntity?.canMarkAsBillableToOwner
                                        }
                                        isBillableToOwnerCheckedPreference={
                                            gridEntity?.isBillableToOwnerCheckedPreference
                                        }
                                        onClose={this.handleCheckedActionClose}
                                        onSendToAccounting={this.handleSendToAccounting}
                                    />
                                )}
                                {updateAccountStatusData && (
                                    <MassUpdateAccountingStatus
                                        actionBeingPerformed={
                                            actionBeingPerformed as BillListFormActions
                                        }
                                        accountingIntegrationName={
                                            gridEntity?.accountingIntegrationName!
                                        }
                                        onCancel={this.handleCheckedActionClose}
                                        onConfirm={this.handleUpdateAccountingStatus}
                                    />
                                )}
                                {this.state.showBulkOcrUploadModal && (
                                    <BulkBillUploadModal
                                        modalConfig={{
                                            parentRoute: match.url,
                                            beforeClose: () => {
                                                this.setState({ showBulkOcrUploadModal: false });
                                            },
                                        }}
                                        onUploadComplete={(batchId, fileNames) => {
                                            this.setState((prev) => ({
                                                uploadTrackerGroups: [
                                                    ...prev.uploadTrackerGroups,
                                                    {
                                                        groupId: `modal-${Date.now()}`,
                                                        batchId,
                                                        fileNames,
                                                        builderId: this.props.builderId,
                                                    },
                                                ],
                                            }));
                                            this.handleTabSelected(TabsFilterTabs.inbox);
                                        }}
                                    />
                                )}
                                {this.state.showEmailModal && (
                                    <EmailModal
                                        onClose={() => this.setState({ showEmailModal: false })}
                                        variant="bills"
                                    />
                                )}
                                {this.state.showPayOnlineMarketingModal && (
                                    <PayOnlineMarketingModal
                                        isVisible={this.state.showPayOnlineMarketingModal}
                                        onClose={() =>
                                            this.setState({ showPayOnlineMarketingModal: false })
                                        }
                                        sourcePage="BillList"
                                    />
                                )}
                                {applyLienWaiverData && (
                                    <ApplyLienWaiver
                                        data={applyLienWaiverData}
                                        actionBeingPerformed={
                                            actionBeingPerformed as BillListFormActions
                                        }
                                        onClose={this.handleCheckedActionClose}
                                    />
                                )}

                                {gridState.dataState === "loading" && <BTLoading />}

                                {gridState.dataState !== "loading" && gridEntity?.listMetadata && (
                                    <ShowOnPortal
                                        builder={
                                            gridEntity.listMetadata.emptyStatesMetadata
                                                .isNewToEntity
                                        }
                                        render={() => (
                                            <ListSection className="padding-bottom-md">
                                                <BillEmptyStateBanner
                                                    isNewToEntity={
                                                        gridEntity.listMetadata.emptyStatesMetadata
                                                            .isNewToEntity
                                                    }
                                                    isReadonly={
                                                        gridEntity.listMetadata.emptyStatesMetadata
                                                            .isReadOnly
                                                    }
                                                    hasListData={gridEntity.listMetadata.hasData}
                                                    onCallToActionClick={() => {}}
                                                    isDismissed={isEmptyStateBannerDismissed}
                                                    onDismiss={this.handleDismissEmptyStateBanner}
                                                    actionBeingPerformed={
                                                        actionBeingPerformed as EmptyStateActions
                                                    }
                                                />
                                            </ListSection>
                                        )}
                                    />
                                )}

                                {filterEntity && (
                                    <FilterDrawer
                                        filterId={entityType}
                                        entity={filterEntity}
                                        onSubmit={this.handleFilterSubmit}
                                        open={this.state.filterDrawerOpen}
                                        setOpen={(open) => {
                                            this.setState({ filterDrawerOpen: !!open });
                                        }}
                                        filterTypeEntity={getFilterEntityType(entityType)}
                                        onSavedFiltersUpdated={(entity: FilterEntity) =>
                                            this.setState({
                                                filterEntity: entity,
                                            })
                                        }
                                        showSaveButton={isBuilder}
                                        showSavedFilters={isBuilder}
                                        appliedFilter={filterReducerState.appliedFilter}
                                        setUnsavedAppliedFilter={this.setUnsavedAppliedFilter}
                                        updateUnsavedChanges={this.updateUnsavedChanges}
                                        showHasUnsavedChanges={
                                            filterReducerState.showHasUnsavedChanges
                                        }
                                        appliedFiltersFromSavedFilter={
                                            filterReducerState.appliedFiltersFromSavedFilter
                                        }
                                    />
                                )}

                                <ListSection>
                                    {/* After the first full data load, keep GridContainer mounted even
                                during subsequent reloads so the stationary tab header (TabsFilter)
                                stays visible instead of flashing away. */}
                                    {(this.state.hasCompletedInitialLoad ||
                                        gridState.dataState !== "loading") &&
                                        gridEntity?.listMetadata &&
                                        gridViews &&
                                        selectedGridView &&
                                        gridSettingsHandler && (
                                            <>
                                                {this.context?.flags?.billsFullPageConversion ? (
                                                    <BillFullPageBanner
                                                        className="margin-top-lg"
                                                        onViewModeChange={(next) =>
                                                            this.setState({ billViewMode: next })
                                                        }
                                                    />
                                                ) : (
                                                    <CostInboxMigrationBanner
                                                        variant="bills"
                                                        className="margin-top-lg"
                                                    />
                                                )}
                                                <InboxDropzoneWrapper
                                                    isActive={isInboxDropzoneActive}
                                                    onFilesDropped={this.handleFilesDropped}
                                                >
                                                    {this.context?.flags.billsAgGridMigration ? (
                                                        <>
                                                            <BillListAgGrid
                                                                data={gridState.data ?? []}
                                                                columnsList={
                                                                    gridState.columnsList ?? []
                                                                }
                                                                footerData={gridState.footerData}
                                                                sortingData={gridState.sortingData.map(
                                                                    (s) => ({
                                                                        field: s.field,
                                                                        dir: s.dir ?? "asc",
                                                                    }),
                                                                )}
                                                                onSortChanged={this.handleSort}
                                                                onColumnsListChange={
                                                                    this.handleAgColumnsListChange
                                                                }
                                                                loading={
                                                                    gridState.dataState ===
                                                                    "loading"
                                                                }
                                                                renderFab={this.renderFab}
                                                                emptyState={this.renderGridEmptyState()}
                                                                hasSelectableRows={isBuilder}
                                                                useAbsoluteLink={
                                                                    this.context?.flags
                                                                        .billsFullPageConversion ??
                                                                    false
                                                                }
                                                                renderToolbarContent={
                                                                    renderBillToolbarContent
                                                                }
                                                                stationaryHeader={
                                                                    this.props
                                                                        .isDraftBillsEffective &&
                                                                    isBuilder && (
                                                                        <>
                                                                            <TabsFilter
                                                                                currentlySelected={
                                                                                    this.state
                                                                                        .selectedTab
                                                                                }
                                                                                onTabSelected={
                                                                                    this
                                                                                        .handleTabSelected
                                                                                }
                                                                                hasDraftOnlyAccess={
                                                                                    gridEntity?.hasDraftOnlyAccess ??
                                                                                    false
                                                                                }
                                                                                counts={this.getTabCounts()}
                                                                                defaultTab={
                                                                                    this.state
                                                                                        .defaultTab
                                                                                }
                                                                                onDefaultTabChanged={
                                                                                    this
                                                                                        .handleDefaultTabChanged
                                                                                }
                                                                            />
                                                                            {isInboxDropzoneActive && (
                                                                                <InboxDropzoneBar
                                                                                    onBrowse={() =>
                                                                                        this.setState(
                                                                                            {
                                                                                                showBulkOcrUploadModal:
                                                                                                    true,
                                                                                            },
                                                                                        )
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </>
                                                                    )
                                                                }
                                                            />
                                                            {gridViews && selectedGridView && (
                                                                <BTModal
                                                                    data-testid="btModalGridSettings"
                                                                    title="Manage Saved Views"
                                                                    setPageTitle={false}
                                                                    width="840px"
                                                                    visible={
                                                                        this.props.settingsState !==
                                                                        "closed"
                                                                    }
                                                                    beforeClose={() =>
                                                                        this.handleSetGridSettingState(
                                                                            "closed",
                                                                        )
                                                                    }
                                                                    removeBodyPadding
                                                                    useModalLayout
                                                                >
                                                                    <SavedViewsAndFiltersSettings
                                                                        dataType="view"
                                                                        viewData={gridViews.map(
                                                                            (v) => v.extraData!,
                                                                        )}
                                                                        columnsList={
                                                                            gridEntity?.columnsListAll
                                                                        }
                                                                        columnsSelectedList={
                                                                            gridState.columnsList
                                                                        }
                                                                        selectedGridView={
                                                                            selectedGridView
                                                                        }
                                                                        onApplyView={async (
                                                                            view,
                                                                        ) => {
                                                                            this.handleSetGridSettingState(
                                                                                "closed",
                                                                            );
                                                                            await this.handleViewApplied(
                                                                                view,
                                                                            );
                                                                        }}
                                                                        onSaveView={
                                                                            this.handleSaveView
                                                                        }
                                                                        onDeleteView={
                                                                            this.handleDeleteView
                                                                        }
                                                                        gridSettingsState={
                                                                            this.props.settingsState
                                                                        }
                                                                        setGridSettingsState={
                                                                            this
                                                                                .handleSetGridSettingState
                                                                        }
                                                                        onSetDefaultView={
                                                                            this
                                                                                .handleSetDefaultView
                                                                        }
                                                                        beforeClose={() =>
                                                                            this.handleSetGridSettingState(
                                                                                "closed",
                                                                            )
                                                                        }
                                                                        sortingData={
                                                                            gridState.sortingData
                                                                        }
                                                                    />
                                                                </BTModal>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <GridContainer
                                                            renderFab={this.renderFab}
                                                            autoHeaderHeight
                                                            gridState={gridState.dataState}
                                                            data={gridState.data}
                                                            columnsList={gridState.columnsList}
                                                            columnsListAll={
                                                                gridEntity.columnsListAll
                                                            }
                                                            sortingData={gridState.sortingData}
                                                            footerData={gridState.footerData}
                                                            onColumnResize={this.handleColumnResize}
                                                            onColumnReorder={
                                                                this.handleColumnReorder
                                                            }
                                                            onColumnFrozen={this.handleColumnFrozen}
                                                            hasSelectableRows={isBuilder}
                                                            onSortChanged={this.handleSort}
                                                            showToolbar
                                                            toolbarViewSettingsConfig={{
                                                                onSave: this.handleSaveView,
                                                                onDelete: this.handleDeleteView,
                                                                onApply: this.handleViewApplied,
                                                                onSetDefaultView:
                                                                    this.handleSetDefaultView,
                                                            }}
                                                            toolbarContentConfig={{
                                                                isVisible: true,
                                                                renderToolbarContent:
                                                                    renderBillToolbarContent,
                                                            }}
                                                            gridViews={gridViews}
                                                            gridEmptyState={this.renderGridEmptyState()}
                                                            selectedGridView={selectedGridView}
                                                            canReorderColumns={isBuilder}
                                                            canFreezeColumns={isBuilder}
                                                            gridSettingsState={
                                                                this.props.settingsState
                                                            }
                                                            setGridSettingsState={
                                                                this.handleSetGridSettingState
                                                            }
                                                            entityType={entityType}
                                                            gridCardView={
                                                                this.context?.flags
                                                                    .mobileWebviewExperience
                                                                    ? GRID_CARD_VIEW_CONFIG
                                                                    : undefined
                                                            }
                                                            scrollRestorationConfig={{
                                                                jobIds: this.props.jobIds,
                                                            }}
                                                            stationaryHeader={
                                                                this.props.isDraftBillsEffective &&
                                                                isBuilder && (
                                                                    <>
                                                                        <TabsFilter
                                                                            currentlySelected={
                                                                                this.state
                                                                                    .selectedTab
                                                                            }
                                                                            onTabSelected={
                                                                                this
                                                                                    .handleTabSelected
                                                                            }
                                                                            hasDraftOnlyAccess={
                                                                                gridEntity?.hasDraftOnlyAccess ??
                                                                                false
                                                                            }
                                                                            counts={this.getTabCounts()}
                                                                            defaultTab={
                                                                                this.state
                                                                                    .defaultTab
                                                                            }
                                                                            onDefaultTabChanged={
                                                                                this
                                                                                    .handleDefaultTabChanged
                                                                            }
                                                                        />
                                                                        {isInboxDropzoneActive && (
                                                                            <InboxDropzoneBar
                                                                                onBrowse={() =>
                                                                                    this.setState({
                                                                                        showBulkOcrUploadModal:
                                                                                            true,
                                                                                    })
                                                                                }
                                                                            />
                                                                        )}
                                                                    </>
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </InboxDropzoneWrapper>
                                                <BTListLayoutWhitespace />
                                                {this.getFooter()}
                                            </>
                                        )}
                                </ListSection>
                                <AddUsersToJob
                                    shouldRenderAddUserToJobModal={
                                        this.state.shouldRenderAddUserToJobModal
                                    }
                                    internalUsersAddedToJob={this.state.internalUsersAddedToJob}
                                    subsAddedToJob={this.state.subsAddedToJob}
                                    jobIds={this.props.jobIds}
                                    handleBeforeClose={this.handleBeforeClose}
                                    handleShowPermissionWizard={this.handleShowPermissionWizard}
                                    history={this.props.history}
                                    location={this.props.location}
                                    match={this.props.match}
                                />
                                {this.state.shouldRenderPermissionWizardModal && (
                                    <PermissionWizard
                                        subIds={this.state.subsAddedToJob.map((s) =>
                                            parseInt(s.id),
                                        )}
                                        jobIds={this.props.jobIds}
                                        internalUserIds={this.state.internalUsersAddedToJob.map(
                                            (u) => parseInt(u.id),
                                        )}
                                        onComplete={this.handleBeforeClose}
                                        onCancel={this.handleBeforeClose}
                                        modalConfig={{
                                            beforeClose: this.handleBeforeClose,
                                            parentRoute: this.props.match.url,
                                        }}
                                    />
                                )}
                                {this.state.uploadTrackerGroups.length > 0 && (
                                    <BulkBillUploadTracker
                                        groups={this.state.uploadTrackerGroups}
                                        onDismiss={() => this.setState({ uploadTrackerGroups: [] })}
                                        onBillProcessed={() => void this.handleReload()}
                                    />
                                )}
                            </>
                        );
                    }}
                />
            </BillLinkAbsoluteContext.Provider>
        );
    }
}

const BillListWithJobs = withSavedViewsSettingsState(
    withJobsitesRequired("Bills")(BillListInternal),
    MANAGE_SAVED_VIEWS,
);

export const BillList = withErrorBoundary<IBillListProps>((props) => {
    const historyState = useHistoryState<IBillListHistoryState>({});
    const [state, dispatch] = useReducer(FilterReducer, defaultFilterState);
    const webviewContext = useWebviewContext();
    const isWebview = webviewContext !== undefined;
    return (
        <UserInfoContext.Consumer>
            {(userInfo) => (
                <BillListWithJobs
                    {...props}
                    filterReducerState={state}
                    filterReducerDispatch={dispatch}
                    userInfo={userInfo}
                    isWebview={isWebview}
                    webviewContext={webviewContext}
                    historyState={historyState}
                />
            )}
        </UserInfoContext.Consumer>
    );
})("Could not load Bills List");
export default BillList;
